import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Archive,
  ArrowLeft,
  BadgeCheck,
  Ban,
  BookOpen,
  Boxes,
  Building2,
  Calendar,
  Check,
  CheckCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Cloud,
  Code2,
  Cpu,
  Database,
  Download,
  Import,
  SquareArrowOutUpRight,
  ExternalLink,
  Eye,
  FileImage,
  FileText,
  Globe,
  HardDrive,
  Layers,
  LayoutGrid,
  List as ListIcon,
  History,
  Link2,
  Loader2,
  MapPin,
  MessageSquare,
  Network,
  Pencil,
  Plus,
  Search,
  Send,
  Server,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Tag,
  Trash2,
  Upload,
  UserCheck,
  UserCog,
  Users,
  X,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router";
import { useTheme } from "next-themes";
import {
  abortImportSession,
  addInformationType,
  patchInformationType,
  deleteInformationType,
  addInventoryItem,
  patchInventoryItem,
  deleteInventoryItem,
  addLeveragedAuthorization,
  addSspComponent,
  addSspControlImplementation,
  addSspInterconnection,
  addSspSystemUser,
  bulkUpdateImplementedRequirements,
  putSspIrSetParameters,
  commitImportSession,
  createSSP,
  createSSPFromApplication,
  createSSPFromService,
  createSspSupportingEvidence,
  deleteLeveragedAuthorization,
  deleteSSP,
  renameDocument,
  deleteSspComponent,
  deleteSspControlImplementation,
  deleteSspInterconnection,
  deleteSspSupportingEvidence,
  deleteSspSystemUser,
  downloadSupportingEvidenceFile,
  exportCatalogDoc,
  getControl,
  getDocument,
  getExportDownloadUrl,
  getExportJob,
  getImportSession,
  getProfileByDocumentId,
  getSspFull,
  getSspProfile,
  getSspProfileMatchedComponents,
  getSystemCharacteristics,
  importComponentToSsp,
  patchSspComponent,
  listComponentControls,
  importSSP,
  listBaselineControls,
  listComponentControlImplementations,
  listComponentInventoryItems,
  listInformationTypes,
  listInventoryItems,
  listLeveragedAuthorizations,
  listCompProfiles,
  listSspComponents,
  listSspControlImplementations,
  listSspInterconnections,
  listSspLocations,
  listSspParties,
  listSspResponsibleParties,
  listSspRoles,
  searchSSPs,
  getAuthHeaders,
  listSspSupportingEvidence,
  listSspSystemUsers,
  previewSupportingEvidenceFile,
  replaceSspLocations,
  replaceSspParties,
  replaceSspResponsibleParties,
  replaceSspRoles,
  resolveAsset,
  setSystemCharacteristics,
  transitionDocument,
  updateImplementedRequirement,
  updateLeveragedAuthorization,
  updateSspInterconnection,
  updateSspSystemUser,
  uploadSspSupportingEvidenceFile,
  validateImport,
  type AssetResolutionResponse,
  type BaselineControlResponse,
  type BulkImplementedRequirementPayload,
  type ComponentDefinitionSummaryResponse,
  type ComponentResponse,
  type ControlOrigination,
  type ControlResponse,
  type DocumentResponse,
  type ImplementationStatus,
  type ImplementedRequirementDetailResponse,
  type ImportComponentsResponse,
  type ImportJobResponse,
  type ImportSessionResponse,
  type InformationTypeResponse,
  type InventoryItemResponse,
  type LeveragedAuthorizationResponse,
  type LeveragedAuthorizationStatus,
  type ProfileDetailResponse,
  type ProfileResponse,
  type SspComponentResponse,
  type SspControlImplementationRawResponse,
  type SspControlImplementationResponse,
  type SspFullResponse,
  type ComponentControlRow,
  type PatchSspComponentPayload,
  type SspInterconnectionResponse,
  type SspLocationPayload,
  type SspLocationResponse,
  type SspPartyPayload,
  type SspPartyResponse,
  type SspResponse,
  type SspResponsiblePartyPayload,
  type SspResponsiblePartyResponse,
  type SspRolePayload,
  type SspRoleResponse,
  type SspSummaryResponse,
  type SspSystemUserResponse,
  type SystemCharacteristicsResponse,
  type ValidateResponse,
} from "../../api/frameworksApi";
import { getAPI, downloadFile } from "../../api/frameworksApi";
import { API_URL } from "../../constants";
import { useToastStore } from "../../stores/toastStore";
import { useUiContextStore } from "../../stores/uiContextStore";
import { useSspInheritance } from "../../hooks/useSspInheritance";
import { RBACButton, RBACAction } from "../components/RBACButton";
import { useRBACPermission } from "../../hooks/useRBACPermission";
import { DataTable } from "../components/DataTable";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { Dropdown } from "../components/Dropdown";
import { StatusBadge } from "../components/StatusBadge";
import {
  EntityCardGrid,
  StatTile,
  StatTiles,
  MetaDates,
  TypeBadge,
  CardActions,
  TitleTooltip,
} from "../components/EntityCardGrid";
import { InheritancePickerModal } from "./SSP/components/InheritancePickerModal";
import { SSPDetailJourney, type JourneyMetrics } from "./SSP/components/SSPDetailJourney";
import { ControlImplementationsTab } from "./SSP/tabs/ControlImplementationsTab";
import {
  FormDatePicker,
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
  Modal,
  ModalFooterButtons,
  ModalShell,
} from "../components/Modal";
import { PageSizeSelector } from "../components/PageSizeSelector";
import { TableToolbar } from "../components/TableToolbar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Tip,
} from "../components/ui/tooltip";
import {
  CONTROL_ORIGINATIONS,
  COMP_DETAIL_PAGE_SIZE,
  COMP_DETAIL_PAGE_SIZE_OPTIONS,
  COMPONENT_TYPES,
  DISPLAY_STATUS_STYLES,
  IMPLEMENTATION_STATUSES,
  LIFECYCLE_STAGES,
  SSP_INHERITANCE_TYPE_OPTIONS,
  SSP_LAYERS,
  SYS_LEVEL_STYLES,
  SYS_STATUS_STYLES,
  SYSTEM_STATUSES,
  TAB_DESCRIPTIONS,
  formatTabName,
  tabs,
  toddmmyyyy,
  type LifecycleStatus,
  type SspInheritanceType,
  type SspLayerKey,
  type TabId,
} from "./SSP/constants";
import {
  formatSecurityLevel,
  getComponentLayer,
  getControlCountLabel,
  getDisplayStatusStyle,
} from "./SSP/utils/components";
import {
  buildParameterRows,
  collectParamRecords,
  findBaselineControlForId,
  getBaselineControlId,
  getNestedParamSource,
  getParamId,
  sourceValues,
  textValues,
  type ParameterDisplayRow,
} from "./SSP/utils/parameters";
import {
  ImpactPill,
  SysCharRow,
  SysLevelPill,
  SysStatusPill,
} from "./SSP/components/SysDisplay";
import { DiagramPreviewModal } from "./SSP/components/DiagramPreviewModal";
import { LoadingSpinner } from "./SSP/components/LoadingSpinner";
import { getPageWindow } from "./SSP/utils/pagination";
import { LeveragedAuthorizationsTab } from "./SSP/tabs/LeveragedAuthorizationsTab";
import { LocationsTab } from "./SSP/tabs/LocationsTab";
import { InventoryTab } from "./SSP/tabs/InventoryTab";
import { InformationTypesTab } from "./SSP/tabs/InformationTypesTab";
import { SystemUsersTab } from "./SSP/tabs/SystemUsersTab";
import { InterconnectionsTab } from "./SSP/tabs/InterconnectionsTab";
import { RolesPartiesTab } from "./SSP/tabs/RolesPartiesTab";
import { ComponentsTab } from "./SSP/tabs/ComponentsTab";
import { SystemCharacteristicsTab } from "./SSP/tabs/SystemCharacteristicsTab";
import { ExportDialog } from "./SSP/components/dialogs/ExportDialog";
import { AddRoleDialog } from "./SSP/components/dialogs/AddRoleDialog";
import { AddPartyDialog } from "./SSP/components/dialogs/AddPartyDialog";
import { AssignResponsiblePartyDialog } from "./SSP/components/dialogs/AssignResponsiblePartyDialog";
import { AddLocationDialog } from "./SSP/components/dialogs/AddLocationDialog";
import { AddInformationTypeDialog } from "./SSP/components/dialogs/AddInformationTypeDialog";
import { AddComponentDialog } from "./SSP/components/dialogs/AddComponentDialog";
import { AddInventoryItemDialog } from "./SSP/components/dialogs/AddInventoryItemDialog";
import { AddLeveragedAuthorizationDialog } from "./SSP/components/dialogs/AddLeveragedAuthorizationDialog";
import { AddSystemUserDialog } from "./SSP/components/dialogs/AddSystemUserDialog";
import { AddInterconnectionDialog } from "./SSP/components/dialogs/AddInterconnectionDialog";
import { LifecycleTransitionDialog } from "./SSP/components/dialogs/LifecycleTransitionDialog";
import { ImportSspDialog } from "./SSP/components/dialogs/ImportSspDialog";
import { ImportSessionDialog } from "./SSP/components/dialogs/ImportSessionDialog";
import { NewSspDialog } from "./SSP/components/dialogs/NewSspDialog";
import { ImportComponentFromLibraryDialog } from "./SSP/components/dialogs/ImportComponentFromLibraryDialog";
import { AddControlImplementationDialog } from "./SSP/components/dialogs/AddControlImplementationDialog";
import { EditControlImplementationDialog } from "./SSP/components/dialogs/EditControlImplementationDialog";
import { PostImportControlStatusDialog } from "./SSP/components/dialogs/PostImportControlStatusDialog";

/**
 * Sortable column key -> backend field name sent as `sortByColumn` in
 * POST /ssps/{page}/{size}. Sorting is fully server-side; a key with no
 * entry here is forwarded as-is.
 */
const SSP_SORT_COLUMNS: Record<string, string> = {
  title: "title",
  profile: "profileName",
  sourceType: "sourceType",
  sourceName: "sourceName",
  status: "lifecycleStatus",
  updated: "updatedAt",
  created: "createdAt",
};

export function SSPEditor() {
  const navigate = useNavigate();
  const { id: sspIdFromUrl } = useParams<{ id: string }>();
  const { theme } = useTheme();
  const isDark = theme !== "light";

  const [activeTab, setActiveTab] = useState<TabId>("System Characteristics");
  const [selectedSSPId, setSelectedSSPId] = useState<string | null>(null);

  // Tab scroll state
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollArrows = useCallback(() => {
    const el = tabsContainerRef.current;
    if (!el) return;
    const sl = Math.round(el.scrollLeft);
    setCanScrollLeft(sl > 0);
    setCanScrollRight(sl + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = tabsContainerRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(updateScrollArrows);
    el.addEventListener("scroll", updateScrollArrows, { passive: true });
    window.addEventListener("resize", updateScrollArrows);
    // ResizeObserver fires when the tab bar renders or its width changes
    const ro = new ResizeObserver(() => updateScrollArrows());
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", updateScrollArrows);
      window.removeEventListener("resize", updateScrollArrows);
      ro.disconnect();
    };
  }, [updateScrollArrows, selectedSSPId]);

  const scrollTabs = (direction: "left" | "right") => {
    const el = tabsContainerRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction === "left" ? -200 : 200,
      behavior: "smooth",
    });
    // Poll to update arrows during smooth scroll animation
    let frame = 0;
    const poll = () => {
      updateScrollArrows();
      frame++;
      if (frame < 20) requestAnimationFrame(poll);
    };
    requestAnimationFrame(poll);
  };

  // List state
  const [sspList, setSspList] = useState<SspSummaryResponse[]>([]);
  const [sspDocTitles, setSspDocTitles] = useState<Record<string, string>>({});

  // Inline SSP title (document) rename — only before publish
  const [editingSspTitle, setEditingSspTitle] = useState(false);
  const [sspTitleDraft, setSspTitleDraft] = useState("");
  const [savingSspTitle, setSavingSspTitle] = useState(false);
  const [sspLifecycles, setSspLifecycles] = useState<
    Record<string, LifecycleStatus>
  >({});
  const [listLoading, setListLoading] = useState(true);
  // True only while a *subsequent* page/sort/search fetch is in flight, so the
  // previous rows stay on screen instead of flashing back to a skeleton.
  const [listRefreshing, setListRefreshing] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // List search + pagination state — search, sorting, filtering and paging are
  // all resolved by the backend via POST /ssps/{page}/{size}; `sspList` always
  // holds exactly the rows for the current page.
  const [sspSearchQuery, setSspSearchQuery] = useState("");
  // The keyword actually sent to the backend (committed on Enter / icon click).
  const [sspKeyword, setSspKeyword] = useState("");
  const [sspStatusFilter, setSspStatusFilter] = useState<string>("");
  const [sspCurrentPage, setSspCurrentPage] = useState(0);
  const [sspTotal, setSspTotal] = useState(0);
  // Each view keeps its own page size so toggling back restores the user's
  // choice instead of snapping to the other view's default.
  const [sspCardPageSize, setSspCardPageSize] = useState(12);
  const [sspTablePageSize, setSspTablePageSize] = useState(10);
  const [sspViewMode, setSspViewMode] = useState<"card" | "list">("card");
  const sspPageSize =
    sspViewMode === "list" ? sspTablePageSize : sspCardPageSize;
  const setSspPageSize = (n: number) => {
    if (sspViewMode === "list") setSspTablePageSize(n);
    else setSspCardPageSize(n);
  };
  // The list has always opened newest-updated first — keep that as the
  // backend's starting sort rather than leaving the order undefined.
  const [sspSortKey, setSspSortKey] = useState<string | null>("updated");
  const [sspSortDir, setSspSortDir] = useState<"asc" | "desc">("desc");

  const handleSspSort = (key: string) => {
    if (sspSortKey === key)
      setSspSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSspSortKey(key);
      setSspSortDir("asc");
    }
    setSspCurrentPage(0);
  };

  const handleSspSearchChange = (query: string) => setSspSearchQuery(query);

  /* ── Search submit ──
   * Typing alone never hits the API: the keyword is only pushed to the
   * backend on Enter / the search button, or the moment the box is cleared. */
  const submitSspSearch = (value: string) => {
    const next = value.trim();
    if (next === sspKeyword) return;
    setSspKeyword(next);
    setSspCurrentPage(0);
  };

  /* ── Rows for the current page ──
   * No client-side filtering, sorting or slicing: the backend already applied
   * the keyword, sort column/order, status filter and page window. */
  const paginatedSspList = sspList;
  /** Serial number continues across pages (page 2 with 12/page starts at 13). */
  const sspRowSerial = (index: number) =>
    sspCurrentPage * sspPageSize + index + 1;

  // SSP card identity: Application vs Service SSPs get a distinct icon +
  // module colour (indigo / violet) while still reading as one SSP family.
  const getSspCardTheme = (ssp: unknown) => {
    const sourceType = (
      (ssp as { sourceType?: string }).sourceType || ""
    ).toLowerCase();
    if (sourceType === "service") {
      return { isService: true, color: "violet" as const, Icon: Server };
    }
    return { isService: false, color: "sky" as const, Icon: Building2 };
  };

  // Detail state
  const [sspDetail, setSspDetail] = useState<SspResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [orgName, setOrgName] = useState<string | null>(null);

  // "New SSP" modal
  const [showNewSSP, setShowNewSSP] = useState(false);
  const [showInheritancePicker, setShowInheritancePicker] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sspForm, setSspForm] = useState({
    title: "",
    shortCode: "",
    importProfileHref: "",
    owningOrgUnitId: "",
  });
  const [sspInheritanceType, setSspInheritanceType] =
    useState<SspInheritanceType>("");
  const [sspSelectedAppId, setSspSelectedAppId] = useState("");
  // Service-mode inheritance is multi-select; application mode stays single.
  const [sspSelectedServiceIds, setSspSelectedServiceIds] = useState<string[]>(
    [],
  );
  const [inheritedIds, setInheritedIds] = useState<string[]>([]);
  const [profiles, setProfiles] = useState<ProfileResponse[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const sspInheritanceMode =
    sspInheritanceType === "application" || sspInheritanceType === "service"
      ? sspInheritanceType
      : null;
  // Bumped on every "New SSP" modal open so the hook refetches and we
  // pick up freshly-created service SSPs (which flip isUsed from false
  // → true on the next /applications response).
  const [sspInheritanceRefreshTick, setSspInheritanceRefreshTick] = useState(0);
  const { data: sspInheritance, loading: sspInheritanceLoading } =
    useSspInheritance(sspInheritanceMode, sspInheritanceRefreshTick);
  const sspApplicationOptions = useMemo(
    () =>
      sspInheritance.applications.map((app) => ({
        // Use applicationId (UUID) as the option value — appCode can
        // collide across applications (e.g. two apps both labelled
        // "A360"), which would auto-select the first match. UUID is
        // guaranteed unique per app.
        value: app.applicationId,
        label: app.applicationName,
      })),
    [sspInheritance.applications],
  );
  const sspServiceOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { value: string; label: string }[] = [];
    for (const app of sspInheritance.applications) {
      for (const svc of app.services ?? []) {
        if (seen.has(svc.serviceId)) continue;
        seen.add(svc.serviceId);
        options.push({ value: svc.serviceId, label: svc.name });
      }
    }
    return options;
  }, [sspInheritance.applications]);
  // For each service of the selected application, find its provider SSP (if any)
  // by cross-referencing sspList on sourceType="service" + sourceId=serviceId.
  const availableProviderSsps = useMemo(() => {
    if (sspInheritanceType !== "application" || !sspSelectedAppId) return [];
    const app = sspInheritance.applications.find(
      (a) => a.applicationId === sspSelectedAppId,
    );
    return (app?.services ?? []).map((svc) => {
      // Match by serviceId (primary) OR by service name (fallback).
      // sourceType is case-insensitive — API may return "SERVICE" or "service".
      const providerSsp = sspList.find(
        (s) =>
          s.sourceType?.toLowerCase() === "service" &&
          (s.sourceId === svc.serviceId ||
            s.sourceName?.toLowerCase() === svc.name?.toLowerCase()),
      );
      const lifecycle = providerSsp
        ? (providerSsp.lifecycleStatus || sspLifecycles[providerSsp.documentId] || "draft").toLowerCase()
        : null;
      const title = providerSsp
        ? (sspDocTitles[providerSsp.documentId] || providerSsp.sourceName || providerSsp.title || providerSsp.documentId)
        : null;
      return {
        serviceId: svc.serviceId,
        serviceName: svc.name,
        sspId: providerSsp?.id ?? null,
        sspTitle: title,
        lifecycleStatus: lifecycle,
        isPublished: lifecycle === "published",
      };
    });
  }, [sspInheritanceType, sspSelectedAppId, sspInheritance.applications, sspList, sspLifecycles, sspDocTitles]);

  const sspTypeSelectionIncomplete =
    !sspInheritanceType ||
    (sspInheritanceType === "application" && !sspSelectedAppId) ||
    (sspInheritanceType === "service" && sspSelectedServiceIds.length === 0);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<SspSummaryResponse | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);

  // Export modal
  const [showExport, setShowExport] = useState(false);
  const [exportingDoc, setExportingDoc] = useState(false);
  const [exportFormat, setExportFormat] = useState("");
  const [exportTargetDocId, setExportTargetDocId] = useState<string | null>(
    null,
  );

  // SSP OSCAL Import pipeline state
  const [showSspImport, setShowSspImport] = useState(false);
  const [sspImportStep, setSspImportStep] = useState<
    "select" | "url" | "importing" | "findings"
  >("select");
  const [sspImportFile, setSspImportFile] = useState<File | null>(null);
  const [sspImportUrl, setSspImportUrl] = useState("");
  const [sspImportMode, setSspImportMode] = useState<"full" | "delta">("full");
  const [sspImportError, setSspImportError] = useState<string | null>(null);
  const [sspImportErrorTitle, setSspImportErrorTitle] = useState<string | null>(
    null,
  );
  const [sspImportResult, setSspImportResult] =
    useState<ImportJobResponse | null>(null);
  const [sspValidateResult, setSspValidateResult] =
    useState<ValidateResponse | null>(null);
  const [sspValidating, setSspValidating] = useState(false);

  // Import Session modal state
  const [showSspSession, setShowSspSession] = useState(false);
  const [sspSessionData, setSspSessionData] =
    useState<ImportSessionResponse | null>(null);
  const [sspSessionLoading, setSspSessionLoading] = useState(false);
  const [sspSessionError, setSspSessionError] = useState<string | null>(null);
  const [sspCommitting, setSspCommitting] = useState(false);
  const [sspAborting, setSspAborting] = useState(false);

  // System Characteristics state
  const [sysChars, setSysChars] =
    useState<SystemCharacteristicsResponse | null>(null);
  const [sysCharsLoading, setSysCharsLoading] = useState(false);
  const [editingSysChars, setEditingSysChars] = useState(false);
  const [savingSysChars, setSavingSysChars] = useState(false);
  const [sysCharsForm, setSysCharsForm] = useState({
    systemName: "",
    systemNameShort: "",
    description: "",
    securitySensitivityLevel: "",
    systemType: "",
    authorizationType: "",
    deploymentModel: "",
    serviceModel: "",
    systemIds: [] as { "identifier-type": string; id: string }[],
    authorizationBoundary: "",
    networkArchitecture: "",
    dataFlow: "",
    confidentiality: "",
    integrity: "",
    availability: "",
    status: "",
    statusRemarks: "",
    dateAuthorized: "",
  });

  // Diagram uploads (backed by supporting-evidence API)
  const [networkArchDiagram, setNetworkArchDiagram] = useState<{
    file?: File;
    evidenceId?: string; // SSP link ID (for delete)
    contentEvidenceId?: string; // content-registry ID (for preview/download)
    fileName?: string;
  } | null>(null);
  const [dataFlowDiagram, setDataFlowDiagram] = useState<{
    file?: File;
    evidenceId?: string;
    contentEvidenceId?: string;
    fileName?: string;
  } | null>(null);
  const [viewingDiagram, setViewingDiagram] = useState<{
    title: string;
    contentEvidenceId: string;
    fileName?: string;
  } | null>(null);
  const [uploadingDiagram, setUploadingDiagram] = useState<string | null>(null); // "network" | "dataflow" | null

  // Information Types state
  const [infoTypes, setInfoTypes] = useState<InformationTypeResponse[]>([]);
  const [infoTypesLoading, setInfoTypesLoading] = useState(false);
  const [showAddInfoType, setShowAddInfoType] = useState(false);
  const [addingInfoType, setAddingInfoType] = useState(false);
  const [infoTypeForm, setInfoTypeForm] = useState({
    title: "",
    description: "",
    confidentiality: "",
    integrity: "",
    availability: "",
  });

  // Components state
  const [sspComponents, setSspComponents] = useState<SspComponentResponse[]>(
    [],
  );
  const [componentsLoading, setComponentsLoading] = useState(false);
  const [showAddComponent, setShowAddComponent] = useState(false);
  const [addingComponent, setAddingComponent] = useState(false);
  const [componentForm, setComponentForm] = useState({
    title: "",
    componentType: "",
    description: "",
    purpose: "",
    status: "",
  });
  const [deletingComponentId, setDeletingComponentId] = useState<string | null>(
    null,
  );
  const [componentDeleteError, setComponentDeleteError] = useState<
    string | null
  >(null);
  const [componentSearchFilter, setComponentSearchFilter] = useState("");
  const [componentTypeFilter, setComponentTypeFilter] = useState("");
  const [componentGroupFilter, setComponentGroupFilter] = useState("");
  // Cylindrical layer view: which SSP layer is currently focused
  const [selectedLayerKey, setSelectedLayerKey] =
    useState<SspLayerKey>("application");
  // Component detail view
  const [selectedComponentUuid, setSelectedComponentUuid] = useState<
    string | null
  >(null);
  const [compDetailTab, setCompDetailTab] = useState<"inventory" | "controls">(
    "inventory",
  );

  // Publish where the user is for the AI Companion: which of this page's three faces is showing, and the
  // record on it. This page holds only ids, so both names are resolved from already-loaded lists — which
  // is why this effect sits down here rather than beside the state it reads. A record whose title has not
  // arrived yet publishes no name, so the companion is never handed a raw uuid as a display name.
  useEffect(() => {
    const store = useUiContextStore.getState();
    const summary = selectedSSPId
      ? sspList.find((ssp) => ssp.id === selectedSSPId)
      : undefined;
    const sspName = summary?.title || sspDocTitles[summary?.documentId ?? ""];
    const component = selectedComponentUuid
      ? sspComponents.find((c) => c.componentUuid === selectedComponentUuid)
      : undefined;

    if (component) {
      store.setPageContext("SSP_COMPONENT_DETAIL", {
        id: String(component.componentUuid),
        name: component.title,
        parentName: sspName,
      });
    } else if (summary && sspName) {
      store.setPageContext("SSP_DETAIL", { id: String(summary.id), name: sspName });
    } else {
      store.setPageContext(selectedSSPId ? "SSP_DETAIL" : "SSP_LIST", null);
    }
    return () => useUiContextStore.getState().clearPageContext();
  }, [
    selectedSSPId,
    sspList,
    sspDocTitles,
    selectedComponentUuid,
    sspComponents,
  ]);
  const [compInventory, setCompInventory] = useState<InventoryItemResponse[]>(
    [],
  );
  const [compControls, setCompControls] = useState<ComponentControlRow[]>([]);
  const [compDetailLoading, setCompDetailLoading] = useState(false);
  // Component detail pagination: assets + controls both paginated client-side
  const [compInvPage, setCompInvPage] = useState(0);
  const [compCtrlPage, setCompCtrlPage] = useState(0);
  const [compPageSize, setCompPageSize] = useState(COMP_DETAIL_PAGE_SIZE);

  // Information Types editing state
  const [editingInfoTypeId, setEditingInfoTypeId] = useState<string | null>(null);
  const [savingInfoType, setSavingInfoType] = useState(false);
  const [editInfoTypeForm, setEditInfoTypeForm] = useState({
    title: "",
    description: "",
    confidentiality: "",
    integrity: "",
    availability: "",
  });

  // Inventory editing state
  const [editingInventoryId, setEditingInventoryId] = useState<string | null>(null);
  const [savingInventoryItem, setSavingInventoryItem] = useState(false);
  const [editInventoryForm, setEditInventoryForm] = useState({
    description: "",
    componentId: "",
    identifiers: [] as { identifierType: string; identifierValue: string }[],
  });

  // Component editing state
  const [editingComponentId, setEditingComponentId] = useState<string | null>(null);
  const [savingComponent, setSavingComponent] = useState(false);
  const [editComponentForm, setEditComponentForm] = useState({
    title: "",
    componentType: "",
    description: "",
    purpose: "",
    status: "",
    statusRemarks: "",
  });

  // Inventory state
  const [inventoryItems, setInventoryItems] = useState<InventoryItemResponse[]>(
    [],
  );
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [addingAsset, setAddingAsset] = useState(false);
  const [assetForm, setAssetForm] = useState({
    componentId: "",
    description: "",
    identifiers: [{ identifierType: "", identifierValue: "" }] as {
      identifierType: string;
      identifierValue: string;
    }[],
  });

  // Control Implementations state
  const [controlImpls, setControlImpls] = useState<
    SspControlImplementationResponse[]
  >([]);
  const [controlImplsLoading, setControlImplsLoading] = useState(false);
  const [controlIdFilter, setControlIdFilter] = useState("");
  const [sspProfile, setSspProfile] = useState<ProfileDetailResponse | null>(
    null,
  );

  // Asset Resolution state
  const [resolveQuery, setResolveQuery] = useState("");
  const [resolving, setResolving] = useState(false);
  const [resolveResult, setResolveResult] =
    useState<AssetResolutionResponse | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);

  // Leveraged Authorizations state
  const [leveragedAuths, setLeveragedAuths] = useState<
    LeveragedAuthorizationResponse[]
  >([]);
  const [leveragedAuthsLoading, setLeveragedAuthsLoading] = useState(false);
  const [showAddLevAuth, setShowAddLevAuth] = useState(false);
  const [addingLevAuth, setAddingLevAuth] = useState(false);
  const [levAuthForm, setLevAuthForm] = useState({
    title: "",
    partyUuid: "",
    dateAuthorized: "",
  });
  const [editingLevAuthId, setEditingLevAuthId] = useState<string | null>(null);
  const [savingLevAuth, setSavingLevAuth] = useState(false);
  const [updatingLevAuthStatusId, setUpdatingLevAuthStatusId] = useState<string | null>(null);
  const [openLevAuthMenuId, setOpenLevAuthMenuId] = useState<string | null>(null);
  const [editLevAuthForm, setEditLevAuthForm] = useState({
    title: "",
    partyUuid: "",
    dateAuthorized: "",
  });
  const [deletingLevAuthId, setDeletingLevAuthId] = useState<string | null>(
    null,
  );

  // System Users state
  const [systemUsers, setSystemUsers] = useState<SspSystemUserResponse[]>([]);
  const [systemUsersLoading, setSystemUsersLoading] = useState(false);
  const [showAddSystemUser, setShowAddSystemUser] = useState(false);
  const [addingSystemUser, setAddingSystemUser] = useState(false);
  const [systemUserForm, setSystemUserForm] = useState({
    title: "",
    shortName: "",
    description: "",
    roleIds: "",
    privilegeTitle: "",
    functionsPerformed: "",
  });
  const [deletingSystemUserId, setDeletingSystemUserId] = useState<
    string | null
  >(null);
  const [editingSystemUserId, setEditingSystemUserId] = useState<string | null>(
    null,
  );
  const [savingSystemUser, setSavingSystemUser] = useState(false);
  const [editSystemUserForm, setEditSystemUserForm] = useState({
    title: "",
    shortName: "",
    description: "",
    roleIds: "",
    privilegeTitle: "",
    functionsPerformed: "",
  });

  // Interconnections state
  const [interconnections, setInterconnections] = useState<
    SspInterconnectionResponse[]
  >([]);
  const [interconnectionsLoading, setInterconnectionsLoading] = useState(false);
  const [showAddInterconnection, setShowAddInterconnection] = useState(false);
  const [addingInterconnection, setAddingInterconnection] = useState(false);
  const defaultInterconnectionForm = {
    title: "",
    description: "",
    direction: "" as "incoming" | "outgoing" | "incoming-outgoing" | "",
    remoteSystemName: "",
    responsibleRoles: "",
    protocols: [
      { name: "", portRanges: [{ start: "", end: "", transport: "tcp" }] },
    ] as {
      name: string;
      portRanges: { start: string; end: string; transport: string }[];
    }[],
  };
  const [interconnectionForm, setInterconnectionForm] = useState(
    defaultInterconnectionForm,
  );
  const [deletingInterconnectionId, setDeletingInterconnectionId] = useState<
    string | null
  >(null);
  const [editingInterconnectionId, setEditingInterconnectionId] = useState<
    string | null
  >(null);
  const [savingInterconnection, setSavingInterconnection] = useState(false);
  const [editInterconnectionForm, setEditInterconnectionForm] = useState({
    ...defaultInterconnectionForm,
  });

  // Baseline Controls state
  const [baselineControls, setBaselineControls] = useState<
    BaselineControlResponse[]
  >([]);
  const [baselineControlsLoading, setBaselineControlsLoading] = useState(false);

  // Roles, Parties state
  const [roles, setRoles] = useState<SspRoleResponse[]>([]);
  const [parties, setParties] = useState<SspPartyResponse[]>([]);
  // Locations state
  const [locations, setLocations] = useState<SspLocationResponse[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [responsibleParties, setResponsibleParties] = useState<
    SspResponsiblePartyResponse[]
  >([]);
  const [rolesPartiesLoading, setRolesPartiesLoading] = useState(false);
  const [showAddRole, setShowAddRole] = useState(false);
  const [addingRole, setAddingRole] = useState(false);
  const [roleForm, setRoleForm] = useState({
    roleId: "",
    title: "",
    shortName: "",
    description: "",
  });
  const [showAddParty, setShowAddParty] = useState(false);
  const [addingParty, setAddingParty] = useState(false);
  const [partyForm, setPartyForm] = useState({
    type: "person" as "person" | "organization",
    name: "",
    shortName: "",
    email: "",
  });
  const [showAssignRP, setShowAssignRP] = useState(false);
  const [assigningRP, setAssigningRP] = useState(false);
  const [rpForm, setRpForm] = useState({ roleId: "", partyUuids: "" });
  const [deletingRoleId, setDeletingRoleId] = useState<string | null>(null);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [savingRole, setSavingRole] = useState(false);
  const [editRoleForm, setEditRoleForm] = useState({
    roleId: "",
    title: "",
    shortName: "",
    description: "",
  });
  const [deletingPartyId, setDeletingPartyId] = useState<string | null>(null);
  const [editingPartyId, setEditingPartyId] = useState<string | null>(null);
  const [savingParty, setSavingParty] = useState(false);
  const [editPartyForm, setEditPartyForm] = useState({
    type: "person" as "person" | "organization",
    name: "",
    shortName: "",
    email: "",
  });
  const [deletingRPId, setDeletingRPId] = useState<string | null>(null);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [addingLocation, setAddingLocation] = useState(false);
  const [locationForm, setLocationForm] = useState({
    title: "",
    addressLines: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
  });
  const [deletingLocationId, setDeletingLocationId] = useState<string | null>(
    null,
  );
  const [editingLocationId, setEditingLocationId] = useState<string | null>(
    null,
  );
  const [savingLocation, setSavingLocation] = useState(false);
  const [editLocationForm, setEditLocationForm] = useState({
    title: "",
    addressLines: "",
    city: "",
    state: "",
    postalCode: "",
    country: "US",
  });

  // ── Tab-level search & pagination state ──
  const [tabSearch, setTabSearch] = useState("");

  // Separate pagination state for each tab
  const [infoTypesPagination, setInfoTypesPagination] = useState({ page: 1, pageSize: 10 });
  const [inventoryPagination, setInventoryPagination] = useState({ page: 1, pageSize: 10 });
  const [levAuthPagination, setLevAuthPagination] = useState({ page: 1, pageSize: 10 });
  const [systemUserPagination, setSystemUserPagination] = useState({ page: 1, pageSize: 10 });
  const [interconnectionPagination, setInterconnectionPagination] = useState({ page: 1, pageSize: 10 });
  const [locationPagination, setLocationPagination] = useState({ page: 1, pageSize: 10 });
  const [controlsPagination, setControlsPagination] = useState({ page: 1, pageSize: 10 });
  const [rolesPagination, setRolesPagination] = useState({ page: 1, pageSize: 10 });
  const [partiesPagination, setPartiesPagination] = useState({ page: 1, pageSize: 10 });
  const [historyPagination, setHistoryPagination] = useState({ page: 1, pageSize: 10 });

  // Helper to get current tab's pagination
  const getTabPagination = () => {
    switch (activeTab) {
      case "Information Types": return infoTypesPagination;
      case "Inventory": return inventoryPagination;
      case "Level Auth": return levAuthPagination;
      case "System Users": return systemUserPagination;
      case "Interconnections": return interconnectionPagination;
      case "Locations": return locationPagination;
      case "Baseline Controls": return controlsPagination;
      case "Roles & Parties": return rolesPagination;
      case "History": return historyPagination;
      default: return { page: 1, pageSize: 10 };
    }
  };

  // Helper to set current tab's pagination
  const setCurrentTabPagination = (pagination: { page: number; pageSize: number }) => {
    switch (activeTab) {
      case "Information Types": return setInfoTypesPagination(pagination);
      case "Inventory": return setInventoryPagination(pagination);
      case "Level Auth": return setLevAuthPagination(pagination);
      case "System Users": return setSystemUserPagination(pagination);
      case "Interconnections": return setInterconnectionPagination(pagination);
      case "Locations": return setLocationPagination(pagination);
      case "Baseline Controls": return setControlsPagination(pagination);
      case "Roles & Parties": return setRolesPagination(pagination);
      case "History": return setHistoryPagination(pagination);
    }
  };

  const tabPage = getTabPagination().page;
  const tabPageSize = getTabPagination().pageSize;
  const setTabPage = (page: number) => setCurrentTabPagination({ ...getTabPagination(), page });
  const setTabPageSize = (pageSize: number) => {
    setCurrentTabPagination({ page: 1, pageSize });
  };

  // Reset tab search when switching tabs (but keep pagination)
  useEffect(() => {
    setTabSearch("");
    setComponentSearchFilter("");
    setControlIdFilter("");
  }, [activeTab]);

  // Sync tabSearch with Components/Control Implementations existing filters
  useEffect(() => {
    if (activeTab === "Components") setComponentSearchFilter(tabSearch);
    if (activeTab === "Control Implementations") setControlIdFilter(tabSearch);
  }, [tabSearch, activeTab]);

  // Control Implementation CRUD state
  const [showAddControlImpl, setShowAddControlImpl] = useState(false);
  const [addingControlImpl, setAddingControlImpl] = useState(false);
  const [controlImplForm, setControlImplForm] = useState({
    componentIds: [] as string[],
    source: "",
    controlId: "",
    description: "",
    implementationStatus: "" as ImplementationStatus | "",
    controlOrigination: "" as ControlOrigination | "",
    responsibleRoleIds: "",
    setParameters: [] as { paramId: string; values: string; remarks: string }[],
    statements: [] as {
      statementId: string;
      description: string;
      sortOrder: string;
    }[],
  });
  // Resolved catalog control selection for Add Control Implementation
  const [profileControlMap, setProfileControlMap] = useState<
    Record<string, { controlName: string; groupName: string }>
  >({});
  const [ciControls, setCiControls] = useState<ControlResponse[]>([]);
  const [ciCatalogId, setCiCatalogId] = useState<string>("");
  const [ciLoadingControls, setCiLoadingControls] = useState(false);
  const [ciSelectedControlDetail, setCiSelectedControlDetail] =
    useState<ControlResponse | null>(null);
  const [ciLoadingControlDetail, setCiLoadingControlDetail] = useState(false);
  const [editingControlImplId, setEditingControlImplId] = useState<
    string | null
  >(null);
  const [editingCiId, setEditingCiId] = useState<string | null>(null);
  /** The single CI id for this SSP — backend enforces one CI per SSP */
  const [singleCiId, setSingleCiId] = useState<string | null>(null);
  const [savingControlImpl, setSavingControlImpl] = useState(false);
  const [showEditControlImpl, setShowEditControlImpl] = useState(false);
  const [editControlImplForm, setEditControlImplForm] = useState({
    controlId: "",
    description: "",
    implementationStatus: "" as ImplementationStatus | "",
    controlOrigination: "" as ControlOrigination | "",
    responsibleRoleIds: "",
    setParameters: [] as { paramId: string; values: string }[],
    byComponents: [] as {
      componentUuid: string;
      description: string;
      implementationStatus: string;
      responsibleRoles: string;
    }[],
    statements: [] as {
      statementId: string;
      description: string;
      sortOrder: number;
    }[],
    sortOrder: 0,
  });
  const [deletingControlImplId, setDeletingControlImplId] = useState<
    string | null
  >(null);

  // Lifecycle state
  const [lifecycleData, setLifecycleData] = useState<DocumentResponse | null>(
    null,
  );
  const [transitioningLifecycle, setTransitioningLifecycle] = useState(false);
  const [showTransitionModal, setShowTransitionModal] = useState(false);
  const [transitionTarget, setTransitionTarget] = useState("");
  const [transitionComment, setTransitionComment] = useState("");

  const normalizeSspLifecycle = (status?: string | null): LifecycleStatus =>
    (status || "draft").toLowerCase() as LifecycleStatus;

  const getSelectedSspLifecycle = (): LifecycleStatus =>
    normalizeSspLifecycle(
      lifecycleData?.lifecycleStatus ||
      (sspDetail?.documentId
        ? sspLifecycles[sspDetail.documentId]
        : undefined),
    );

  const isSelectedSspPublished = () =>
    getSelectedSspLifecycle() === "published";

  const getSspSummaryLifecycle = (ssp: SspSummaryResponse | null) =>
    normalizeSspLifecycle(
      ssp?.lifecycleStatus ||
      (ssp?.documentId ? sspLifecycles[ssp.documentId] : undefined),
    );

  // Organization state for SSP import
  const [accessibleOrgs, setAccessibleOrgs] = useState<any[]>([]);
  const [accessibleOrgsLoading, setAccessibleOrgsLoading] = useState(false);
  const [importOrgId, setImportOrgId] = useState("");

  // CDEF Import state
  const [showImportComponent, setShowImportComponent] = useState(false);
  const [cdefList, setCdefList] = useState<
    ComponentDefinitionSummaryResponse[]
  >([]);
  const [cdefComponents, setCdefComponents] = useState<
    (ComponentResponse & { cdefTitle: string })[]
  >([]);
  const [cdefLoading, setCdefLoading] = useState(false);
  const [selectedImportComponentIds, setSelectedImportComponentIds] = useState<
    Set<string>
  >(new Set());
  const [importingComponents, setImportingComponents] = useState(false);
  const [importedComponentIds, setImportedComponentIds] = useState<Set<string>>(
    new Set(),
  );

  // Post-import control status popup
  const [postImportIRs, setPostImportIRs] = useState<
    {
      ciId: string;
      componentId: string;
      componentTitle: string;
      ir: ImplementedRequirementDetailResponse;
    }[]
  >([]);
  const [postImportComponentTitle, setPostImportComponentTitle] = useState("");
  const [showPostImportPopup, setShowPostImportPopup] = useState(false);
  const [postImportStatuses, setPostImportStatuses] = useState<
    Record<string, ImplementationStatus>
  >({});
  const [postImportDescriptions, setPostImportDescriptions] = useState<
    Record<string, string>
  >({});
  const [postImportStmtEdits, setPostImportStmtEdits] = useState<
    Record<
      string,
      { statementId: string; description: string; sortOrder: number }[]
    >
  >({});
  const [postImportControlDetails, setPostImportControlDetails] = useState<
    Record<string, ControlResponse>
  >({});
  const [postImportParamEdits, setPostImportParamEdits] = useState<
    Record<string, { paramId: string; values: string }[]>
  >({});
  const [savingIrId, setSavingIrId] = useState<string | null>(null);
  const [savedIrIds, setSavedIrIds] = useState<Set<string>>(new Set());
  const [postImportLoading, setPostImportLoading] = useState(false);
  const [postImportLoadingControlId, setPostImportLoadingControlId] = useState<
    string | null
  >(null);
  const [postImportSelectedIdx, setPostImportSelectedIdx] = useState<
    number | null
  >(null);
  const [postImportSearch, setPostImportSearch] = useState("");
  const [excludedIrIds, setExcludedIrIds] = useState<Set<string>>(new Set());

  // Generic delete confirmation
  const [pendingDelete, setPendingDelete] = useState<{
    type: string;
    id: string;
    title: string;
    action: () => Promise<void>;
  } | null>(null);
  const [pendingDeleting, setPendingDeleting] = useState(false);

  const confirmPendingDelete = async () => {
    if (!pendingDelete) return;
    setPendingDeleting(true);
    try {
      await pendingDelete.action();
    } finally {
      setPendingDeleting(false);
      setPendingDelete(null);
    }
  };

  const requestDelete = (
    type: string,
    id: string,
    title: string,
    action: () => Promise<void>,
  ) => {
    if (isSelectedSspPublished()) {
      useToastStore
        .getState()
        .showError(
          "Error",
          "Published SSPs are read-only and cannot be changed",
        );
      return;
    }
    setPendingDelete({ type, id, title, action });
  };

  // Global error
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (error) {
      useToastStore.getState().showError("Error", error);
      setError(null);
    }
  }, [error]);

  // ── Fetch SSP list (server-side search / sort / filter / paginate) ──────
  // The skeleton is only shown before the very first page ever lands; every
  // later fetch keeps the current rows visible (no blank page on paging).
  const sspFirstLoadDoneRef = useRef(false);
  const fetchSSPList = useCallback(async () => {
    setListError(null);
    if (sspFirstLoadDoneRef.current) setListRefreshing(true);
    else setListLoading(true);
    try {
      const res = await searchSSPs(sspCurrentPage, sspPageSize, {
        keyword: sspKeyword.trim(),
        sortByColumn: sspSortKey
          ? (SSP_SORT_COLUMNS[sspSortKey] ?? sspSortKey)
          : "",
        sortingOrder: sspSortKey ? sspSortDir : "",
        lifecycleStatus: sspStatusFilter || null,
      });
      setSspList(res.items);
      setSspTotal(res.totalItems);
      // The current page can fall off the end of the result set (a delete, a
      // larger page size, a narrower filter) — step back to the last real page.
      if (res.items.length === 0 && sspCurrentPage > 0 && res.totalItems > 0) {
        setSspCurrentPage(
          Math.max(0, Math.ceil(res.totalItems / sspPageSize) - 1),
        );
      }
      // Use title and lifecycleStatus directly from the SSP list response.
      // Merge rather than replace: only the current page's rows come back now,
      // and the detail view still needs the title/status of an SSP the user
      // opened from an earlier page.
      setSspDocTitles((prev) => ({
        ...prev,
        ...Object.fromEntries(
          res.items.map((ssp) => [ssp.documentId, ssp.title || ssp.documentId]),
        ),
      }));
      setSspLifecycles((prev) => ({
        ...prev,
        ...Object.fromEntries(
          res.items.map((ssp) => [
            ssp.documentId,
            normalizeSspLifecycle(ssp.lifecycleStatus),
          ]),
        ),
      }));
    } catch (err) {
      setListError(err instanceof Error ? err.message : "Failed to load SSPs");
      setSspList([]);
      setSspTotal(0);
    } finally {
      sspFirstLoadDoneRef.current = true;
      setListLoading(false);
      setListRefreshing(false);
    }
  }, [
    sspCurrentPage,
    sspPageSize,
    sspKeyword,
    sspSortKey,
    sspSortDir,
    sspStatusFilter,
  ]);

  useEffect(() => {
    fetchSSPList();
  }, [fetchSSPList]);

  // ── Fetch accessible organizations ───────────────────────────────────
  const fetchAccessibleOrgs = async (module: string, action: string) => {
    setAccessibleOrgsLoading(true);
    try {
      const res = await getAPI(
        `${API_URL.ACCESSIBLE_ORGS}?module=${module}&action=${action}&org_type=ORGANIZATION&org_type=GROUP`,
      );
      const orgs = res?.data?.organizations ?? [];
      setAccessibleOrgs(orgs);
      if (orgs.length === 1) {
        setImportOrgId(orgs[0].id);
      }
    } catch {
      setAccessibleOrgs([]);
    } finally {
      setAccessibleOrgsLoading(false);
    }
  };

  // ── Fetch SSP detail (single /full call) ───────────────
  const fetchSSPDetail = useCallback(async (id: string) => {
    // Guard: Validate input parameter
    if (!id || typeof id !== "string" || id.trim().length === 0) {
      setError("Invalid SSP ID provided");
      setDetailLoading(false);
      return;
    }

    setDetailLoading(true);
    setError(null);
    try {
      // Guard: Validate API response envelope shape
      const res = await getSspFull(id);
      if (!res?.data) {
        throw new Error("Invalid SSP response: missing data envelope");
      }

      const full: SspFullResponse = res.data;
      if (!full?.ssp) {
        throw new Error("Invalid SSP response: missing SSP object");
      }

      const { ssp } = full;

      setSspDetail(ssp);

      // Fetch document details to get organization name
      // Only fetch if documentId exists and is non-empty string
      if (typeof ssp.documentId === "string" && ssp.documentId.trim().length > 0) {
        try {
          const docRes = await getDocument(ssp.documentId);
          // Guard: Validate document response and normalize owningOrgUnitName
          const fetchedOrgName = docRes?.data?.owningOrgUnitName?.trim() || null;
          setOrgName(fetchedOrgName);
        } catch (docErr) {
          // Best-effort: Log the error but don't fail the entire SSP load
          console.warn("Failed to fetch document organization details:", docErr);
          setOrgName(null);
        }
      } else {
        // No documentId: clear org name
        setOrgName(null);
      }

      setSysChars(ssp.systemCharacteristics ?? null);
      setSspComponents(ssp.systemImplementation?.components ?? []);
      setInventoryItems(ssp.systemImplementation?.inventoryItems ?? []);
      setSystemUsers(ssp.systemImplementation?.users ?? []);
      setInterconnections(ssp.systemImplementation?.interconnections ?? []);
      setLeveragedAuths(ssp.leveragedAuthorizations ?? []);
      setInfoTypes(full.informationTypes ?? []);
      setRoles(full.roles ?? []);
      setParties(full.parties ?? []);
      setLocations(full.locations ?? []);
      setResponsibleParties(full.responsibleParties ?? []);
      const rawBaseline = full.baselineControls as any;
      setBaselineControls(Array.isArray(rawBaseline) ? rawBaseline : (rawBaseline?.controls ?? []));

      // Flatten raw control implementations (same logic as listSspControlImplementations)
      const flatCIs: SspControlImplementationResponse[] = [];
      for (const ci of ssp.controlImplementations ?? []) {
        for (const ir of ci.implementedRequirements ?? []) {
          flatCIs.push({
            id: ir.id,
            irUuid: ir.irUuid,
            controlId: ir.controlId,
            componentId: ci.componentId ?? null,
            source: ci.source,
            description: ir.description ?? ci.description,
            implementationStatus: ir.implementationStatus,
            controlOrigination: ir.origination,
            setParameters: ir.setParameters ?? [],
            byComponents: ir.byComponents ?? [],
            statements: ir.statements ?? [],
            sortOrder: ir.sortOrder,
            createdAt: ir.createdAt,
            updatedAt: ir.updatedAt,
            ciId: ci.id,
          });
        }
      }
      setControlImpls(flatCIs);
    } catch (err: any) {
      // Guard: Extract error detail from various response shapes
      const apiError = err?.response?.data?.errors?.[0]?.detail;
      const errorMsg = apiError ?? (err instanceof Error ? err.message : "Failed to load SSP details");
      setError(errorMsg);
      // Reset org name on error to avoid stale state
      setOrgName(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  /* ── Load SSP from URL parameter on mount ── */
  useEffect(() => {
    if (!sspIdFromUrl) return;

    let cancelled = false;

    const loadSSPFromUrl = async () => {
      const existingSSP = sspList.find((s) => s.id === sspIdFromUrl);
      if (existingSSP && !cancelled) {
        setSelectedSSPId(sspIdFromUrl);
        fetchSSPDetail(sspIdFromUrl);
      } else if (!cancelled) {
        // Directly set the ID and let the existing useEffect handle fetching
        setSelectedSSPId(sspIdFromUrl);
        fetchSSPDetail(sspIdFromUrl);
      }
    };

    loadSSPFromUrl();
    return () => {
      cancelled = true;
    };
  }, [sspIdFromUrl, sspList, fetchSSPDetail]);

  useEffect(() => {
    if (selectedSSPId) {
      fetchSSPDetail(selectedSSPId);
    }
  }, [selectedSSPId, fetchSSPDetail]);

  // Fetch lifecycle once we have the document ID from SSP detail
  useEffect(() => {
    if (sspDetail?.documentId) {
      fetchLifecycle(sspDetail.documentId);
    }
  }, [sspDetail?.documentId]);

  // ── Fetch tab data when tab changes ─────────────────────
  // Most data is pre-loaded by fetchSSPDetail via GET /ssps/{id}/full.
  // Supporting Evidence is not included in /full so is still fetched on tab visit.
  // Component UUID selection is reset when navigating to Components.
  useEffect(() => {
    if (!selectedSSPId) return;
    if (activeTab === "System Characteristics") {
      fetchSupportingEvidence(selectedSSPId);
    } else if (activeTab === "Components") {
      setSelectedComponentUuid(null);
      setComponentDeleteError(null);
    }
  }, [activeTab, selectedSSPId]);

  // ── Tab data fetchers ───────────────────────────────────
  const fetchSystemCharacteristics = async (id: string) => {
    setSysCharsLoading(true);
    try {
      const res = await getSystemCharacteristics(id);
      setSysChars(res.data);
    } catch {
      setSysChars(null);
    } finally {
      setSysCharsLoading(false);
    }
  };

  const fetchInformationTypes = async (id: string) => {
    setInfoTypesLoading(true);
    try {
      const res = await listInformationTypes(id);
      setInfoTypes(res.data);
    } catch {
      setInfoTypes([]);
    } finally {
      setInfoTypesLoading(false);
    }
  };

  const fetchComponents = async (id: string) => {
    setComponentsLoading(true);
    try {
      const res = await listSspComponents(id);
      setSspComponents(res.data);
    } catch {
      setSspComponents([]);
    } finally {
      setComponentsLoading(false);
    }
  };

  const fetchInventory = async (id: string) => {
    setInventoryLoading(true);
    try {
      const res = await listInventoryItems(id);
      setInventoryItems(res.data);
    } catch {
      setInventoryItems([]);
    } finally {
      setInventoryLoading(false);
    }
  };

  const fetchControlImplementations = async (
    id: string,
    controlId?: string,
  ) => {
    setControlImplsLoading(true);
    try {
      const res = await listSspControlImplementations(id, controlId);
      setControlImpls(res.data);
      // Backend enforces one CI per SSP — capture it for reuse
      if (res.data.length > 0 && res.data[0].ciId) {
        setSingleCiId(res.data[0].ciId);
      }
    } catch {
      setControlImpls([]);
    } finally {
      setControlImplsLoading(false);
    }
  };

  const fetchSspProfile = async () => {
    if (!sspDetail?.importProfileHref) return;
    const documentId = sspDetail.importProfileHref.replace(/^#/, "");
    try {
      const res = await getProfileByDocumentId(documentId);
      setSspProfile(res.data);
    } catch {
      setSspProfile(null);
    }
  };

  const fetchLeveragedAuthorizations = async (id: string) => {
    setLeveragedAuthsLoading(true);
    try {
      const res = await listLeveragedAuthorizations(id);
      setLeveragedAuths(res.data);
    } catch {
      setLeveragedAuths([]);
    } finally {
      setLeveragedAuthsLoading(false);
    }
  };

  const fetchLocations = async (id: string) => {
    setLocationsLoading(true);
    try {
      const res = await listSspLocations(id);
      setLocations(res.data);
    } catch {
      setLocations([]);
    } finally {
      setLocationsLoading(false);
    }
  };

  const fetchRolesAndParties = async (id: string) => {
    setRolesPartiesLoading(true);
    try {
      const [rolesRes, partiesRes, rpRes] = await Promise.all([
        listSspRoles(id),
        listSspParties(id),
        listSspResponsibleParties(id),
      ]);
      setRoles(rolesRes.data);
      setParties(partiesRes.data);
      setResponsibleParties(rpRes.data);
    } catch {
      setRoles([]);
      setParties([]);
      setResponsibleParties([]);
    } finally {
      setRolesPartiesLoading(false);
    }
  };

  const fetchSystemUsers = async (id: string) => {
    setSystemUsersLoading(true);
    try {
      const res = await listSspSystemUsers(id);
      setSystemUsers(res.data);
    } catch {
      setSystemUsers([]);
    } finally {
      setSystemUsersLoading(false);
    }
  };

  const fetchInterconnections = async (id: string) => {
    setInterconnectionsLoading(true);
    try {
      const res = await listSspInterconnections(id);
      setInterconnections(res.data);
    } catch {
      setInterconnections([]);
    } finally {
      setInterconnectionsLoading(false);
    }
  };

  const fetchBaselineControls = async (id: string) => {
    setBaselineControlsLoading(true);
    try {
      const res = await listBaselineControls(id);
      const raw = res.data as any;
      setBaselineControls(Array.isArray(raw) ? raw : (raw?.controls ?? []));
    } catch {
      setBaselineControls([]);
    } finally {
      setBaselineControlsLoading(false);
    }
  };

  useEffect(() => {
    if (!showEditControlImpl || !selectedSSPId) return;

    let cancelled = false;
    listBaselineControls(selectedSSPId)
      .then((res) => {
        if (!cancelled) {
          const raw = res.data as any;
          setBaselineControls(Array.isArray(raw) ? raw : (raw?.controls ?? []));
        }
      })
      .catch(() => {
        // Keep the existing baseline data if a modal refresh fails.
      });

    return () => {
      cancelled = true;
    };
  }, [showEditControlImpl, selectedSSPId, editControlImplForm.controlId]);

  const fetchLifecycle = async (documentId: string) => {
    try {
      const res = await getDocument(documentId);
      setLifecycleData(res.data);
    } catch {
      setLifecycleData(null);
    }
  };

  // ── Fetch Profiles for SSP creation ─────────────────────
  const fetchProfiles = async () => {
    setLoadingProfiles(true);
    try {
      const res = await listCompProfiles();
      setProfiles(res.data);
    } catch {
      setProfiles([]);
    } finally {
      setLoadingProfiles(false);
    }
  };

  // ── Create SSP ──────────────────────────────────────────
  // Application-mode picks route through POST /ssps/from-application; the
  // plain endpoint is used for service mode (no API contract yet) and any
  // bare creation path. owning_org_unit_id comes from the chosen
  // organisation (form value or the only accessible org).
  const handleCreateSSP = async (
    inheritance?: {
      applicationId?: string;
      serviceIds?: string[];
      componentIds?: string[];
      providerSspIds?: string[];
    },
    // NewSspDialog keeps the form local while open (so typing doesn't
    // re-render this whole page) and hands the values over on submit. Fall
    // back to state for the inheritance-picker path, which creates later.
    formOverride?: typeof sspForm,
  ) => {
    const draft = formOverride ?? sspForm;
    if (
      !draft.title ||
      !draft.shortCode.trim() ||
      !draft.importProfileHref ||
      sspTypeSelectionIncomplete
    )
      return;
    setCreating(true);
    try {
      const owningOrgUnitId =
        draft.owningOrgUnitId ||
        (accessibleOrgs.length === 1 ? accessibleOrgs[0].id : undefined);
      let createdId: string | null = null;
      if (inheritance?.applicationId && owningOrgUnitId) {
        const res = await createSSPFromApplication({
          title: draft.title,
          ssp_short_code: draft.shortCode.trim(),
          import_profile_href: draft.importProfileHref,
          owning_org_unit_id: owningOrgUnitId,
          application_id: inheritance.applicationId,
          component_ids: inheritance.componentIds ?? [],
          ...(inheritance.providerSspIds?.length
            ? { provider_ssp_ids: inheritance.providerSspIds }
            : {}),
        });
        createdId = res.data?.id ?? null;
      } else if (inheritance?.serviceIds?.length && owningOrgUnitId) {
        await createSSPFromService({
          title: draft.title,
          ssp_short_code: draft.shortCode.trim(),
          import_profile_href: draft.importProfileHref,
          owning_org_unit_id: owningOrgUnitId,
          // Service mode is a single pick — the BE contract takes one
          // service_id, so send the first (and only) selected service.
          service_id: inheritance.serviceIds[0],
          component_ids: inheritance.componentIds ?? [],
        });
      } else {
        await createSSP({
          title: draft.title,
          importProfileHref: draft.importProfileHref,
          owningOrgUnitId,
        });
      }
      setShowNewSSP(false);
      setSspForm({ title: "", shortCode: "", importProfileHref: "", owningOrgUnitId: "" });
      setSspInheritanceType("");
      setSspSelectedAppId("");
      setSspSelectedServiceIds([]);
      // Invalidate the cached inheritance data — the just-created SSP
      // changes isUsed/usedBySsps for one of the services.
      setSspInheritanceRefreshTick((t) => t + 1);
      await fetchSSPList();
      // Open the new SSP directly (kept in state; the URL stays on the list path).
      if (createdId) {
        setSelectedSSPId(createdId);
        setActiveTab("System Characteristics");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create SSP");
    } finally {
      setCreating(false);
    }
  };

  // ── Delete SSP ──────────────────────────────────────────
  const handleDeleteSSP = async () => {
    if (!deleteTarget) return;
    if (getSspSummaryLifecycle(deleteTarget) === "published") {
      useToastStore
        .getState()
        .showError("Error", "Published SSPs cannot be deleted");
      setDeleteTarget(null);
      return;
    }
    setDeleting(true);
    try {
      await deleteSSP(deleteTarget.id, "Deleted from SSP Editor");
      useToastStore
        .getState()
        .showSuccess("Success", `SSP "${deleteTarget.title}" deleted`);
      setDeleteTarget(null);
      if (selectedSSPId === deleteTarget.id) {
        setSelectedSSPId(null);
        setSspDetail(null);
        setOrgName(null);
      }
      await fetchSSPList();
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      setError(detail ?? (err instanceof Error ? err.message : "Failed to delete SSP"));
    } finally {
      setDeleting(false);
    }
  };

  // ── Save System Characteristics ─────────────────────────
  const handleSaveSysChars = async () => {
    if (!selectedSSPId || !sysCharsForm.systemName) return;
    setSavingSysChars(true);
    try {
      const res = await setSystemCharacteristics(selectedSSPId, {
        systemName: sysCharsForm.systemName,
        systemNameShort: sysCharsForm.systemNameShort || undefined,
        description: sysCharsForm.description || undefined,
        securitySensitivityLevel:
          sysCharsForm.securitySensitivityLevel || undefined,
        systemType: sysCharsForm.systemType || undefined,
        authorizationType: sysCharsForm.authorizationType || undefined,
        deploymentModel: sysCharsForm.deploymentModel || undefined,
        serviceModel: sysCharsForm.serviceModel || undefined,
        systemIds:
          sysCharsForm.systemIds.length > 0
            ? sysCharsForm.systemIds.filter((s) => s.id)
            : [],
        authorizationBoundary: sysCharsForm.authorizationBoundary
          ? { description: sysCharsForm.authorizationBoundary }
          : undefined,
        networkArchitecture: sysCharsForm.networkArchitecture
          ? { description: sysCharsForm.networkArchitecture }
          : undefined,
        dataFlow: sysCharsForm.dataFlow
          ? { description: sysCharsForm.dataFlow }
          : undefined,
        securityImpactLevel:
          sysCharsForm.confidentiality ||
            sysCharsForm.integrity ||
            sysCharsForm.availability
            ? {
              "security-objective-confidentiality":
                sysCharsForm.confidentiality || "low",
              "security-objective-integrity": sysCharsForm.integrity || "low",
              "security-objective-availability":
                sysCharsForm.availability || "low",
            }
            : undefined,
        status: sysCharsForm.status || undefined,
        statusRemarks: sysCharsForm.statusRemarks || undefined,
        dateAuthorized: sysCharsForm.dateAuthorized
          ? `${sysCharsForm.dateAuthorized}T00:00:00.000000000Z`
          : undefined,
      });
      setSysChars(res.data);
      setEditingSysChars(false);
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `System characteristics for "${sysCharsForm.systemName}" saved`,
        );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to save system characteristics",
      );
    } finally {
      setSavingSysChars(false);
    }
  };

  // ── Fetch existing supporting evidence (diagrams) ──────
  const fetchSupportingEvidence = useCallback(async (sspId: string) => {
    try {
      const res = await listSspSupportingEvidence(sspId);
      const evidenceList = res.data || [];
      for (const ev of evidenceList) {
        if (ev.tags?.includes("network-architecture") && ev.hasFile) {
          setNetworkArchDiagram({
            evidenceId: ev.id,
            contentEvidenceId: ev.evidenceId,
            fileName: ev.fileName || ev.title,
          });
        }
        if (ev.tags?.includes("data-flow") && ev.hasFile) {
          setDataFlowDiagram({
            evidenceId: ev.id,
            contentEvidenceId: ev.evidenceId,
            fileName: ev.fileName || ev.title,
          });
        }
      }
    } catch {
      // Evidence not available yet — not critical
    }
  }, []);

  // ── Upload diagram via supporting-evidence API ────────
  const handleDiagramUpload = async (
    file: File,
    type: "network" | "dataflow",
  ) => {
    if (!selectedSSPId) return;
    const tag = type === "network" ? "network-architecture" : "data-flow";
    const title =
      type === "network" ? "Network Architecture Diagram" : "Data Flow Diagram";
    setUploadingDiagram(type);
    try {
      // Step 1: Create the evidence record
      const createRes = await createSspSupportingEvidence(selectedSSPId, {
        title,
        description: `${title} for this SSP`,
        tags: [tag],
      });
      const linkId = createRes.data.id;
      const contentEvidenceId = createRes.data.evidenceId;

      // Step 2: Upload the file
      await uploadSspSupportingEvidenceFile(selectedSSPId, linkId, file);

      const diagramInfo = {
        file,
        evidenceId: linkId,
        contentEvidenceId,
        fileName: file.name,
      };
      if (type === "network") {
        setNetworkArchDiagram(diagramInfo);
      } else {
        setDataFlowDiagram(diagramInfo);
      }
      useToastStore
        .getState()
        .showSuccess("Success", `Uploaded "${file.name}" for ${title}`);
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : `Failed to upload ${title}`,
        );
    } finally {
      setUploadingDiagram(null);
    }
  };

  // ── Delete diagram evidence ───────────────────────────
  const handleDiagramDelete = async (type: "network" | "dataflow") => {
    if (!selectedSSPId) return;
    const diagram = type === "network" ? networkArchDiagram : dataFlowDiagram;
    if (!diagram?.evidenceId) {
      // Local-only, just clear
      if (diagram?.file) URL.revokeObjectURL(diagram.url);
      if (type === "network") setNetworkArchDiagram(null);
      else setDataFlowDiagram(null);
      return;
    }
    try {
      await deleteSspSupportingEvidence(selectedSSPId, diagram.evidenceId);
      if (diagram.file) URL.revokeObjectURL(diagram.url);
      if (type === "network") setNetworkArchDiagram(null);
      else setDataFlowDiagram(null);
      useToastStore.getState().showSuccess("Success", "Diagram removed");
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to remove diagram",
        );
    }
  };

  // ── Add Information Type ────────────────────────────────
  const handleAddInfoType = async () => {
    if (!selectedSSPId || !infoTypeForm.title) return;
    setAddingInfoType(true);
    try {
      await addInformationType(selectedSSPId, {
        title: infoTypeForm.title,
        description: infoTypeForm.description || undefined,
        confidentialityImpact: infoTypeForm.confidentiality
          ? { base: infoTypeForm.confidentiality }
          : undefined,
        integrityImpact: infoTypeForm.integrity
          ? { base: infoTypeForm.integrity }
          : undefined,
        availabilityImpact: infoTypeForm.availability
          ? { base: infoTypeForm.availability }
          : undefined,
        sortOrder: infoTypes.length,
      });
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `Information type "${infoTypeForm.title}" added`,
        );
      setShowAddInfoType(false);
      setInfoTypeForm({
        title: "",
        description: "",
        confidentiality: "",
        integrity: "",
        availability: "",
      });
      await fetchInformationTypes(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to add information type",
      );
    } finally {
      setAddingInfoType(false);
    }
  };

  // ── Add Component ───────────────────────────────────────
  const handleAddComponent = async () => {
    if (!selectedSSPId || !componentForm.title || !componentForm.componentType)
      return;
    setAddingComponent(true);
    try {
      await addSspComponent(selectedSSPId, {
        title: componentForm.title,
        componentType: componentForm.componentType,
        description: componentForm.description || undefined,
        purpose: componentForm.purpose || undefined,
        status: componentForm.status || undefined,
        sortOrder: sspComponents.length,
      });
      useToastStore
        .getState()
        .showSuccess("Success", `Component "${componentForm.title}" added`);
      setShowAddComponent(false);
      setComponentForm({
        title: "",
        componentType: "",
        description: "",
        purpose: "",
        status: "",
      });
      await fetchComponents(selectedSSPId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add component");
    } finally {
      setAddingComponent(false);
    }
  };

  // ── Delete Component ────────────────────────────────────
  const handleDeleteComponent = async (componentUuid: string) => {
    if (!selectedSSPId) return;
    setDeletingComponentId(componentUuid);
    setComponentDeleteError(null);
    try {
      await deleteSspComponent(selectedSSPId, componentUuid);
      useToastStore.getState().showSuccess("Success", "Component deleted");
      await fetchComponents(selectedSSPId);
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      const msg = detail ?? (err instanceof Error ? err.message : "Failed to delete component");
      setComponentDeleteError(msg);
    } finally {
      setDeletingComponentId(null);
    }
  };

  // ── Component Detail View ─────────────────────────────
  const handleOpenComponentDetail = async (componentId: string) => {
    if (!selectedSSPId) return;
    setSelectedComponentUuid(componentId);
    setCompDetailTab("inventory");
    setCompDetailLoading(true);
    setCompInvPage(0);
    setCompCtrlPage(0);
    try {
      const [invRes, ctrlRes] = await Promise.all([
        listComponentInventoryItems(selectedSSPId, componentId),
        listComponentControls(selectedSSPId, componentId),
      ]);
      setCompInventory(invRes.data ?? []);
      setCompControls(ctrlRes.data ?? []);
    } catch {
      setCompInventory([]);
      setCompControls([]);
    } finally {
      setCompDetailLoading(false);
    }
  };

  // ── Edit Component ─────────────────────────────────────
  const handleUpdateComponent = async () => {
    if (!selectedSSPId || !editingComponentId || !editComponentForm.title) return;
    setSavingComponent(true);
    try {
      const payload: PatchSspComponentPayload = {
        title: editComponentForm.title,
        componentType: editComponentForm.componentType || undefined,
        description: editComponentForm.description || null,
        purpose: editComponentForm.purpose || null,
        status: editComponentForm.status || null,
        statusRemarks: editComponentForm.statusRemarks || null,
      };
      await patchSspComponent(selectedSSPId, editingComponentId, payload);
      useToastStore.getState().showSuccess("Success", `Component "${editComponentForm.title}" updated`);
      setEditingComponentId(null);
      await fetchComponents(selectedSSPId);
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      setError(detail ?? (err instanceof Error ? err.message : "Failed to update component"));
    } finally {
      setSavingComponent(false);
    }
  };

  // ── Edit Information Type ──────────────────────────────
  const handleUpdateInfoType = async () => {
    if (!selectedSSPId || !editingInfoTypeId || !editInfoTypeForm.title) return;
    setSavingInfoType(true);
    try {
      await patchInformationType(selectedSSPId, editingInfoTypeId, {
        title: editInfoTypeForm.title,
        description: editInfoTypeForm.description || undefined,
        confidentialityImpact: editInfoTypeForm.confidentiality ? { base: editInfoTypeForm.confidentiality } : undefined,
        integrityImpact: editInfoTypeForm.integrity ? { base: editInfoTypeForm.integrity } : undefined,
        availabilityImpact: editInfoTypeForm.availability ? { base: editInfoTypeForm.availability } : undefined,
      });
      useToastStore.getState().showSuccess("Success", `Information type "${editInfoTypeForm.title}" updated`);
      setEditingInfoTypeId(null);
      await fetchInformationTypes(selectedSSPId);
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      setError(detail ?? (err instanceof Error ? err.message : "Failed to update information type"));
    } finally {
      setSavingInfoType(false);
    }
  };

  const handleDeleteInfoType = async (infoTypeId: string) => {
    if (!selectedSSPId) return;
    try {
      await deleteInformationType(selectedSSPId, infoTypeId);
      useToastStore.getState().showSuccess("Success", "Information type deleted");
      await fetchInformationTypes(selectedSSPId);
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      setError(detail ?? (err instanceof Error ? err.message : "Failed to delete information type"));
    }
  };

  // ── Edit Inventory Item ────────────────────────────────
  const handleUpdateInventoryItem = async () => {
    if (!selectedSSPId || !editingInventoryId || !editInventoryForm.description) return;
    setSavingInventoryItem(true);
    try {
      const selectedComp = sspComponents.find((c) => c.id === editInventoryForm.componentId);
      await patchInventoryItem(selectedSSPId, editingInventoryId, {
        description: editInventoryForm.description,
        component_id: editInventoryForm.componentId || undefined,
        implementedComponents: selectedComp ? [{ "component-uuid": selectedComp.componentUuid }] : undefined,
        assetIdentifiers: editInventoryForm.identifiers.filter((i) => i.identifierType && i.identifierValue),
      });
      useToastStore.getState().showSuccess("Success", "Inventory item updated");
      setEditingInventoryId(null);
      await fetchInventory(selectedSSPId);
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      setError(detail ?? (err instanceof Error ? err.message : "Failed to update inventory item"));
    } finally {
      setSavingInventoryItem(false);
    }
  };

  const handleDeleteInventoryItem = async (itemId: string) => {
    if (!selectedSSPId) return;
    try {
      await deleteInventoryItem(selectedSSPId, itemId);
      useToastStore.getState().showSuccess("Success", "Inventory item deleted");
      await fetchInventory(selectedSSPId);
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      setError(detail ?? (err instanceof Error ? err.message : "Failed to delete inventory item"));
    }
  };

  // ── Add Inventory Item ──────────────────────────────────
  const handleAddAsset = async () => {
    if (!selectedSSPId || !assetForm.description || !assetForm.componentId)
      return;
    setAddingAsset(true);
    try {
      const selectedComp = sspComponents.find(
        (c) => c.id === assetForm.componentId,
      );
      await addInventoryItem(selectedSSPId, {
        description: assetForm.description,
        component_id: assetForm.componentId,
        implementedComponents: selectedComp
          ? [{ "component-uuid": selectedComp.componentUuid }]
          : [],
        assetIdentifiers: assetForm.identifiers.filter(
          (id) => id.identifierType && id.identifierValue,
        ),
        sortOrder: inventoryItems.length,
      });
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `Inventory item "${assetForm.description}" added`,
        );
      setShowAddAsset(false);
      setAssetForm({
        componentId: "",
        description: "",
        identifiers: [{ identifierType: "", identifierValue: "" }],
      });
      await fetchInventory(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to add inventory item",
      );
    } finally {
      setAddingAsset(false);
    }
  };

  // ── Resolve Asset ───────────────────────────────────────
  const handleResolveAsset = async () => {
    if (!selectedSSPId || !resolveQuery) return;
    setResolving(true);
    setResolveError(null);
    setResolveResult(null);
    try {
      const res = await resolveAsset(selectedSSPId, resolveQuery);
      setResolveResult(res.data);
    } catch (err) {
      setResolveError(
        err instanceof Error ? err.message : "Failed to resolve asset",
      );
    } finally {
      setResolving(false);
    }
  };

  // ── Add Leveraged Authorization ────────────────────────
  const handleAddLevAuth = async () => {
    if (!selectedSSPId || !levAuthForm.title) return;
    setAddingLevAuth(true);
    try {
      await addLeveragedAuthorization(selectedSSPId, {
        leveragedUuid: null,
        title: levAuthForm.title,
        partyUuid: levAuthForm.partyUuid || undefined,
        dateAuthorized: levAuthForm.dateAuthorized
          ? `${levAuthForm.dateAuthorized}T00:00:00.000000000Z`
          : undefined,
        sortOrder: leveragedAuths.length,
      });
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `Leveraged authorization "${levAuthForm.title}" added`,
        );
      setShowAddLevAuth(false);
      setLevAuthForm({
        title: "",
        partyUuid: "",
        dateAuthorized: "",
      });
      await fetchLeveragedAuthorizations(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to add leveraged authorization",
      );
    } finally {
      setAddingLevAuth(false);
    }
  };

  // ── Update Leveraged Authorization ────────────────────
  const handleUpdateLevAuth = async () => {
    if (!selectedSSPId || !editingLevAuthId || !editLevAuthForm.title) return;
    setSavingLevAuth(true);
    try {
      await updateLeveragedAuthorization(selectedSSPId, editingLevAuthId, {
        title: editLevAuthForm.title,
        partyUuid: editLevAuthForm.partyUuid || undefined,
        dateAuthorized: editLevAuthForm.dateAuthorized
          ? `${editLevAuthForm.dateAuthorized}T00:00:00.000000000Z`
          : undefined,
      });
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `Leveraged authorization "${editLevAuthForm.title}" updated`,
        );
      setEditingLevAuthId(null);
      await fetchLeveragedAuthorizations(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to update leveraged authorization",
      );
    } finally {
      setSavingLevAuth(false);
    }
  };

  // ── Update Leveraged Authorization Status ─────────────
  const handleUpdateLevAuthStatus = async (authId: string, status: LeveragedAuthorizationStatus) => {
    if (!selectedSSPId) return;
    setUpdatingLevAuthStatusId(authId);
    try {
      await updateLeveragedAuthorization(selectedSSPId, authId, { status });
      setLeveragedAuths((prev) => prev.map((a) => a.id === authId ? { ...a, status } : a));
      useToastStore.getState().showSuccess("Updated", `Authorization marked as ${status}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update authorization status");
    } finally {
      setUpdatingLevAuthStatusId(null);
    }
  };

  // ── Delete Leveraged Authorization ────────────────────
  const handleDeleteLevAuth = async (authId: string) => {
    if (!selectedSSPId) return;
    setDeletingLevAuthId(authId);
    try {
      await deleteLeveragedAuthorization(selectedSSPId, authId);
      useToastStore
        .getState()
        .showSuccess("Success", "Leveraged authorization deleted");
      await fetchLeveragedAuthorizations(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to delete leveraged authorization",
      );
    } finally {
      setDeletingLevAuthId(null);
    }
  };

  // ── Roles & Parties CRUD (PUT replace-all) ─────────────
  const handleAddRole = async () => {
    if (!selectedSSPId || !roleForm.roleId || !roleForm.title) return;
    setAddingRole(true);
    try {
      const newRoles: SspRolePayload[] = [
        ...roles.map((r) => ({
          roleId: r.roleId,
          title: r.title,
          shortName: r.shortName,
          description: r.description,
          sortOrder: r.sortOrder,
        })),
        {
          roleId: roleForm.roleId,
          title: roleForm.title,
          shortName: roleForm.shortName || null,
          description: roleForm.description || null,
          sortOrder: roles.length,
        },
      ];
      await replaceSspRoles(selectedSSPId, newRoles);
      useToastStore
        .getState()
        .showSuccess("Success", `Role "${roleForm.title}" added`);
      setShowAddRole(false);
      setRoleForm({ roleId: "", title: "", shortName: "", description: "" });
      await fetchRolesAndParties(selectedSSPId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add role");
    } finally {
      setAddingRole(false);
    }
  };

  const handleDeleteRole = async (roleDbId: string) => {
    if (!selectedSSPId) return;
    setDeletingRoleId(roleDbId);
    try {
      const newRoles: SspRolePayload[] = roles
        .filter((r) => r.id !== roleDbId)
        .map((r, i) => ({
          roleId: r.roleId,
          title: r.title,
          shortName: r.shortName,
          description: r.description,
          sortOrder: i,
        }));
      await replaceSspRoles(selectedSSPId, newRoles);
      useToastStore.getState().showSuccess("Success", "Role deleted");
      await fetchRolesAndParties(selectedSSPId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete role");
    } finally {
      setDeletingRoleId(null);
    }
  };

  const handleAddParty = async () => {
    if (!selectedSSPId || !partyForm.name) return;
    setAddingParty(true);
    try {
      const newParties: SspPartyPayload[] = [
        ...parties.map((p) => ({
          partyUuid: p.partyUuid,
          partyType: p.partyType,
          name: p.name,
          shortName: p.shortName,
          emailAddresses: p.emailAddresses,
          telephoneNumbers: p.telephoneNumbers,
          memberOfOrganizations: p.memberOfOrganizations,
          externalIds: p.externalIds,
          sortOrder: p.sortOrder,
        })),
        {
          partyType: partyForm.type,
          name: partyForm.name,
          shortName: partyForm.shortName || null,
          emailAddresses: partyForm.email ? [partyForm.email] : [],
          telephoneNumbers: [],
          memberOfOrganizations: [],
          sortOrder: parties.length,
        },
      ];
      await replaceSspParties(selectedSSPId, newParties);
      useToastStore
        .getState()
        .showSuccess("Success", `Party "${partyForm.name}" added`);
      setShowAddParty(false);
      setPartyForm({ type: "person", name: "", shortName: "", email: "" });
      await fetchRolesAndParties(selectedSSPId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add party");
    } finally {
      setAddingParty(false);
    }
  };

  const handleDeleteParty = async (partyDbId: string) => {
    if (!selectedSSPId) return;
    setDeletingPartyId(partyDbId);
    try {
      const newParties: SspPartyPayload[] = parties
        .filter((p) => p.id !== partyDbId)
        .map((p, i) => ({
          partyUuid: p.partyUuid,
          partyType: p.partyType,
          name: p.name,
          shortName: p.shortName,
          emailAddresses: p.emailAddresses,
          telephoneNumbers: p.telephoneNumbers,
          memberOfOrganizations: p.memberOfOrganizations,
          externalIds: p.externalIds,
          sortOrder: i,
        }));
      await replaceSspParties(selectedSSPId, newParties);
      useToastStore.getState().showSuccess("Success", "Party deleted");
      await fetchRolesAndParties(selectedSSPId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete party");
    } finally {
      setDeletingPartyId(null);
    }
  };

  const handleAssignRP = async () => {
    if (!selectedSSPId || !rpForm.roleId || !rpForm.partyUuids) return;
    setAssigningRP(true);
    try {
      const newEntry: SspResponsiblePartyPayload = {
        roleId: rpForm.roleId,
        partyUuids: rpForm.partyUuids
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      };
      const existing: SspResponsiblePartyPayload[] = responsibleParties
        .filter((rp) => rp.roleId !== rpForm.roleId)
        .map((rp) => ({ roleId: rp.roleId, partyUuids: rp.partyUuids }));
      await replaceSspResponsibleParties(selectedSSPId, [
        ...existing,
        newEntry,
      ]);
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `Responsible party assigned to role "${rpForm.roleId}"`,
        );
      setShowAssignRP(false);
      setRpForm({ roleId: "", partyUuids: "" });
      await fetchRolesAndParties(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to assign responsible party",
      );
    } finally {
      setAssigningRP(false);
    }
  };

  const handleDeleteRP = async (rpId: string) => {
    if (!selectedSSPId) return;
    setDeletingRPId(rpId);
    try {
      const newRPs: SspResponsiblePartyPayload[] = responsibleParties
        .filter((rp) => rp.id !== rpId)
        .map((rp) => ({ roleId: rp.roleId, partyUuids: rp.partyUuids }));
      await replaceSspResponsibleParties(selectedSSPId, newRPs);
      useToastStore
        .getState()
        .showSuccess("Success", "Responsible party assignment removed");
      await fetchRolesAndParties(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to remove assignment",
      );
    } finally {
      setDeletingRPId(null);
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedSSPId || !editingRoleId || !editRoleForm.title) return;
    setSavingRole(true);
    try {
      const newRoles: SspRolePayload[] = roles.map((r) => {
        if (r.id === editingRoleId) {
          return {
            roleId: r.roleId,
            title: editRoleForm.title,
            shortName: editRoleForm.shortName || null,
            description: editRoleForm.description || null,
            sortOrder: r.sortOrder,
          };
        }
        return {
          roleId: r.roleId,
          title: r.title,
          shortName: r.shortName,
          description: r.description,
          sortOrder: r.sortOrder,
        };
      });
      await replaceSspRoles(selectedSSPId, newRoles);
      useToastStore
        .getState()
        .showSuccess("Success", `Role "${editRoleForm.title}" updated`);
      setEditingRoleId(null);
      await fetchRolesAndParties(selectedSSPId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update role");
    } finally {
      setSavingRole(false);
    }
  };

  const handleUpdateParty = async () => {
    if (!selectedSSPId || !editingPartyId || !editPartyForm.name) return;
    setSavingParty(true);
    try {
      const newParties: SspPartyPayload[] = parties.map((p) => {
        if (p.id === editingPartyId) {
          return {
            partyUuid: p.partyUuid,
            partyType: editPartyForm.type,
            name: editPartyForm.name,
            shortName: editPartyForm.shortName || null,
            emailAddresses: editPartyForm.email ? [editPartyForm.email] : [],
            telephoneNumbers: p.telephoneNumbers,
            memberOfOrganizations: p.memberOfOrganizations,
            externalIds: p.externalIds,
            sortOrder: p.sortOrder,
          };
        }
        return {
          partyUuid: p.partyUuid,
          partyType: p.partyType,
          name: p.name,
          shortName: p.shortName,
          emailAddresses: p.emailAddresses,
          telephoneNumbers: p.telephoneNumbers,
          memberOfOrganizations: p.memberOfOrganizations,
          externalIds: p.externalIds,
          sortOrder: p.sortOrder,
        };
      });
      await replaceSspParties(selectedSSPId, newParties);
      useToastStore
        .getState()
        .showSuccess("Success", `Party "${editPartyForm.name}" updated`);
      setEditingPartyId(null);
      await fetchRolesAndParties(selectedSSPId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update party");
    } finally {
      setSavingParty(false);
    }
  };

  // ── Locations CRUD (PUT replace-all) ────────────────────
  const handleAddLocation = async () => {
    if (!selectedSSPId || !locationForm.title) return;
    setAddingLocation(true);
    try {
      const newLocations: SspLocationPayload[] = [
        ...locations.map((l) => ({
          locationUuid: l.locationUuid,
          title: l.title,
          addressLines: l.addressLines,
          city: l.city,
          state: l.state,
          postalCode: l.postalCode,
          country: l.country,
          latitude: l.latitude,
          longitude: l.longitude,
          sortOrder: l.sortOrder,
        })),
        {
          title: locationForm.title,
          addressLines: locationForm.addressLines
            ? locationForm.addressLines.split("\n").filter(Boolean)
            : [],
          city: locationForm.city || null,
          state: locationForm.state || null,
          postalCode: locationForm.postalCode || null,
          country: locationForm.country || null,
          sortOrder: locations.length,
        },
      ];
      await replaceSspLocations(selectedSSPId, newLocations);
      useToastStore
        .getState()
        .showSuccess("Success", `Location "${locationForm.title}" added`);
      setShowAddLocation(false);
      setLocationForm({
        title: "",
        addressLines: "",
        city: "",
        state: "",
        postalCode: "",
        country: "US",
      });
      await fetchLocations(selectedSSPId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add location");
    } finally {
      setAddingLocation(false);
    }
  };

  const handleDeleteLocation = async (locationDbId: string) => {
    if (!selectedSSPId) return;
    setDeletingLocationId(locationDbId);
    try {
      const newLocations: SspLocationPayload[] = locations
        .filter((l) => l.id !== locationDbId)
        .map((l, i) => ({
          locationUuid: l.locationUuid,
          title: l.title,
          addressLines: l.addressLines,
          city: l.city,
          state: l.state,
          postalCode: l.postalCode,
          country: l.country,
          latitude: l.latitude,
          longitude: l.longitude,
          sortOrder: i,
        }));
      await replaceSspLocations(selectedSSPId, newLocations);
      useToastStore.getState().showSuccess("Success", "Location deleted");
      await fetchLocations(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to delete location",
      );
    } finally {
      setDeletingLocationId(null);
    }
  };

  const handleUpdateLocation = async () => {
    if (!selectedSSPId || !editingLocationId || !editLocationForm.title) return;
    setSavingLocation(true);
    try {
      const newLocations: SspLocationPayload[] = locations.map((l) => {
        if (l.id === editingLocationId) {
          return {
            locationUuid: l.locationUuid,
            title: editLocationForm.title,
            addressLines: editLocationForm.addressLines
              ? editLocationForm.addressLines.split("\n").filter(Boolean)
              : [],
            city: editLocationForm.city || null,
            state: editLocationForm.state || null,
            postalCode: editLocationForm.postalCode || null,
            country: editLocationForm.country || null,
            sortOrder: l.sortOrder,
          };
        }
        return {
          locationUuid: l.locationUuid,
          title: l.title,
          addressLines: l.addressLines,
          city: l.city,
          state: l.state,
          postalCode: l.postalCode,
          country: l.country,
          latitude: l.latitude,
          longitude: l.longitude,
          sortOrder: l.sortOrder,
        };
      });
      await replaceSspLocations(selectedSSPId, newLocations);
      useToastStore
        .getState()
        .showSuccess("Success", `Location "${editLocationForm.title}" updated`);
      setEditingLocationId(null);
      await fetchLocations(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update location",
      );
    } finally {
      setSavingLocation(false);
    }
  };

  // ── System Users CRUD ──────────────────────────────────
  const handleAddSystemUser = async () => {
    if (!selectedSSPId || !systemUserForm.title) return;
    setAddingSystemUser(true);
    try {
      const authorizedPrivileges = systemUserForm.privilegeTitle
        ? [
          {
            title: systemUserForm.privilegeTitle,
            "functions-performed": systemUserForm.functionsPerformed
              ? systemUserForm.functionsPerformed
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
              : [],
          },
        ]
        : [];
      await addSspSystemUser(selectedSSPId, {
        title: systemUserForm.title,
        shortName: systemUserForm.shortName || undefined,
        description: systemUserForm.description || undefined,
        roleIds: systemUserForm.roleIds
          ? systemUserForm.roleIds
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
          : [],
        authorizedPrivileges,
        sortOrder: systemUsers.length,
      });
      useToastStore
        .getState()
        .showSuccess("Success", `System user "${systemUserForm.title}" added`);
      setShowAddSystemUser(false);
      setSystemUserForm({
        title: "",
        shortName: "",
        description: "",
        roleIds: "",
        privilegeTitle: "",
        functionsPerformed: "",
      });
      await fetchSystemUsers(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to add system user",
      );
    } finally {
      setAddingSystemUser(false);
    }
  };

  const handleDeleteSystemUser = async (userId: string) => {
    if (!selectedSSPId) return;
    setDeletingSystemUserId(userId);
    try {
      await deleteSspSystemUser(selectedSSPId, userId);
      useToastStore.getState().showSuccess("Success", "System user deleted");
      await fetchSystemUsers(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to delete system user",
      );
    } finally {
      setDeletingSystemUserId(null);
    }
  };

  const handleUpdateSystemUser = async () => {
    if (!selectedSSPId || !editingSystemUserId || !editSystemUserForm.title)
      return;
    setSavingSystemUser(true);
    try {
      const authorizedPrivileges = editSystemUserForm.privilegeTitle
        ? [
          {
            title: editSystemUserForm.privilegeTitle,
            functions_performed: editSystemUserForm.functionsPerformed
              ? editSystemUserForm.functionsPerformed
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
              : [],
          },
        ]
        : undefined;
      await updateSspSystemUser(selectedSSPId, editingSystemUserId, {
        title: editSystemUserForm.title,
        shortName: editSystemUserForm.shortName || undefined,
        description: editSystemUserForm.description || undefined,
        roleIds: editSystemUserForm.roleIds
          ? editSystemUserForm.roleIds
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
          : undefined,
        authorizedPrivileges,
      });
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `System user "${editSystemUserForm.title}" updated`,
        );
      setEditingSystemUserId(null);
      await fetchSystemUsers(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update system user",
      );
    } finally {
      setSavingSystemUser(false);
    }
  };

  // ── Interconnections CRUD ─────────────────────────────
  const handleAddInterconnection = async () => {
    if (!selectedSSPId || !interconnectionForm.title) return;
    setAddingInterconnection(true);
    try {
      const filteredProtocols = interconnectionForm.protocols
        .filter((p) => p.name.trim())
        .map((p) => ({
          name: p.name,
          port_ranges: p.portRanges
            .filter((pr) => pr.start)
            .map((pr) => ({
              start: Number(pr.start),
              end: Number(pr.end || pr.start),
              transport: pr.transport || "tcp",
            })),
        }));
      const filteredRoles = interconnectionForm.responsibleRoles
        .split(",")
        .map((r) => r.trim())
        .filter(Boolean)
        .map((r) => ({ role_id: r }));
      await addSspInterconnection(selectedSSPId, {
        title: interconnectionForm.title,
        description: interconnectionForm.description || undefined,
        direction:
          (interconnectionForm.direction as
            | "incoming"
            | "outgoing"
            | "incoming-outgoing") || "outgoing",
        remoteSystemName: interconnectionForm.remoteSystemName || undefined,
        responsibleRoles: filteredRoles.length > 0 ? filteredRoles : undefined,
        protocols: filteredProtocols.length > 0 ? filteredProtocols : undefined,
        sortOrder: interconnections.length,
      });
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `Interconnection "${interconnectionForm.title}" added`,
        );
      setShowAddInterconnection(false);
      setInterconnectionForm({
        ...defaultInterconnectionForm,
        protocols: [
          { name: "", portRanges: [{ start: "", end: "", transport: "tcp" }] },
        ],
      });
      await fetchInterconnections(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to add interconnection",
      );
    } finally {
      setAddingInterconnection(false);
    }
  };

  const handleDeleteInterconnection = async (connId: string) => {
    if (!selectedSSPId) return;
    setDeletingInterconnectionId(connId);
    try {
      await deleteSspInterconnection(selectedSSPId, connId);
      useToastStore
        .getState()
        .showSuccess("Success", "Interconnection deleted");
      await fetchInterconnections(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to delete interconnection",
      );
    } finally {
      setDeletingInterconnectionId(null);
    }
  };

  const handleUpdateInterconnection = async () => {
    if (
      !selectedSSPId ||
      !editingInterconnectionId ||
      !editInterconnectionForm.title
    )
      return;
    setSavingInterconnection(true);
    try {
      const filteredProtocols = editInterconnectionForm.protocols
        .filter((p) => p.name.trim())
        .map((p) => ({
          name: p.name,
          port_ranges: p.portRanges
            .filter((pr) => pr.start)
            .map((pr) => ({
              start: Number(pr.start),
              end: Number(pr.end || pr.start),
              transport: pr.transport || "tcp",
            })),
        }));
      const filteredRoles = editInterconnectionForm.responsibleRoles
        .split(",")
        .map((r) => r.trim())
        .filter(Boolean)
        .map((r) => ({ role_id: r }));
      await updateSspInterconnection(selectedSSPId, editingInterconnectionId, {
        title: editInterconnectionForm.title,
        description: editInterconnectionForm.description || undefined,
        direction:
          (editInterconnectionForm.direction as
            | "incoming"
            | "outgoing"
            | "incoming-outgoing") || undefined,
        remoteSystemName: editInterconnectionForm.remoteSystemName || undefined,
        responsibleRoles: filteredRoles.length > 0 ? filteredRoles : undefined,
        protocols: filteredProtocols.length > 0 ? filteredProtocols : undefined,
      });
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `Interconnection "${editInterconnectionForm.title}" updated`,
        );
      setEditingInterconnectionId(null);
      await fetchInterconnections(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update interconnection",
      );
    } finally {
      setSavingInterconnection(false);
    }
  };

  // ── Fetch controls from profile for Add Control Implementation ──
  const fetchCiResolvedControls = async () => {
    setCiControls([]);
    setCiCatalogId("");
    setProfileControlMap({});
    setControlImplForm((f) => ({
      ...f,
      controlId: "",
      description: "",
      setParameters: [],
      statements: [],
    }));
    if (!selectedSSPId) return;
    setCiLoadingControls(true);
    try {
      // Use /ssps/{id}/profile to get the linked profile directly
      const profileRes = await getSspProfile(selectedSSPId);
      const profileDetail = profileRes.data;
      setControlImplForm((f) => ({
        ...f,
        source: sspDetail?.importProfileHref || "",
      }));
      // Extract catalog ID from profile imports
      const catalogId = profileDetail.imports?.[0]?.href?.replace(/^#/, "");
      if (catalogId) setCiCatalogId(catalogId);

      // Build control name map from controlDetails
      const controlMap: Record<
        string,
        { controlName: string; groupName: string }
      > = {};
      for (const imp of profileDetail.imports || []) {
        for (const cd of (imp as any).controlDetails || []) {
          if (cd.controlName) {
            controlMap[cd.controlId] = {
              controlName: cd.controlName,
              groupName: cd.groupName || "",
            };
          }
        }
      }
      setProfileControlMap(controlMap);

      // Build controls list: prefer controlDetails, fallback to with-ids
      const controls: ControlResponse[] = [];
      for (const imp of profileDetail.imports || []) {
        const details = (imp as any).controlDetails || [];
        if (details.length > 0) {
          // Use controlDetails - skip group-level IDs (those without controlName)
          for (const cd of details) {
            if (cd.controlName) {
              controls.push({
                id: cd.controlId,
                controlId: cd.controlId,
                title: cd.controlName,
              } as ControlResponse);
            }
          }
        } else {
          // Fallback: use with-ids from includeControls
          for (const ic of imp.includeControls || []) {
            for (const controlId of ic["with-ids"] || []) {
              controls.push({
                id: controlId,
                controlId,
                title:
                  controlMap[controlId]?.controlName || controlId.toUpperCase(),
              } as ControlResponse);
            }
          }
        }
      }

      // Deduplicate and sort controls
      const uniqueControls = Array.from(
        new Map(controls.map((c) => [c.controlId, c])).values(),
      ).sort((a, b) => a.controlId.localeCompare(b.controlId));
      setCiControls(uniqueControls);
    } catch {
      setCiControls([]);
    } finally {
      setCiLoadingControls(false);
    }
  };

  // ── Control Implementation CRUD ──────────────────────────
  const handleAddControlImpl = async () => {
    if (
      !selectedSSPId ||
      controlImplForm.componentIds.length === 0 ||
      !controlImplForm.source ||
      !controlImplForm.controlId ||
      !controlImplForm.implementationStatus
    )
      return;
    setAddingControlImpl(true);
    try {
      for (const compId of controlImplForm.componentIds) {
        await addSspControlImplementation(selectedSSPId, {
          source: controlImplForm.source,
          controlId: controlImplForm.controlId,
          component_id: compId,
          description: controlImplForm.description || undefined,
          implementationStatus:
            controlImplForm.implementationStatus as ImplementationStatus,
          controlOrigination:
            (controlImplForm.controlOrigination as ControlOrigination) ||
            undefined,
          responsibleRoleIds: controlImplForm.responsibleRoleIds
            ? controlImplForm.responsibleRoleIds
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
            : undefined,
          setParameters: controlImplForm.setParameters
            .filter((p) => p.paramId && p.values)
            .map((p) => ({
              "param-id": p.paramId,
              values: p.values
                .split(",")
                .map((v) => v.trim())
                .filter(Boolean),
              remarks: p.remarks || undefined,
            })),
          statements: controlImplForm.statements
            .filter((s) => s.statementId)
            .map((s) => ({
              statementId: s.statementId,
              description: s.description || undefined,
              sortOrder: s.sortOrder ? Number(s.sortOrder) : undefined,
            })),
        });
      }
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `Control implementation for "${controlImplForm.controlId}" added`,
        );
      setShowAddControlImpl(false);
      setControlImplForm({
        componentIds: [],
        source: "",
        controlId: "",
        description: "",
        implementationStatus: "",
        controlOrigination: "",
        responsibleRoleIds: "",
        setParameters: [],
        statements: [],
      });
      setCiControls([]);
      setCiSelectedControlDetail(null);
      await fetchControlImplementations(
        selectedSSPId,
        controlIdFilter || undefined,
      );
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      setError(detail ?? (err instanceof Error ? err.message : "Failed to add control implementation"));
    } finally {
      setAddingControlImpl(false);
    }
  };

  const handleUpdateControlImpl = async () => {
    if (
      !selectedSSPId ||
      !editControlImplForm.controlId ||
      !editControlImplForm.implementationStatus
    )
      return;

    setSavingControlImpl(true);
    try {
      const payload: BulkImplementedRequirementPayload[] = [
        {
          controlId: editControlImplForm.controlId,
          implementationStatus:
            editControlImplForm.implementationStatus as ImplementationStatus,
          origination:
            (editControlImplForm.controlOrigination as ControlOrigination) ||
            undefined,
          description: editControlImplForm.description || undefined,
          responsibleRoles: editControlImplForm.responsibleRoleIds
            ? editControlImplForm.responsibleRoleIds
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
              .map((roleId) => ({ "role-id": roleId }))
            : undefined,
          byComponents:
            editControlImplForm.byComponents.length > 0
              ? editControlImplForm.byComponents
                .filter((bc) => bc.componentUuid)
                .map((bc, idx) => ({
                  componentUuid: bc.componentUuid,
                  description: bc.description || undefined,
                  implementationStatus: bc.implementationStatus || undefined,
                  sortOrder: idx,
                }))
              : [],
          statements:
            editControlImplForm.statements.length > 0
              ? editControlImplForm.statements
                .filter((s) => s.statementId)
                .map((s) => ({
                  statementId: s.statementId,
                  description: s.description || undefined,
                  sortOrder: s.sortOrder,
                }))
              : [],
          sortOrder: editControlImplForm.sortOrder,
        },
      ];

      // Use the ciId captured when the modal opened; fall back to searching controlImpls
      let ciId: string | undefined =
        editingCiId ||
        (editingControlImplId
          ? controlImpls.find((ci) => ci.id === editingControlImplId)?.ciId
          : controlImpls.find(
            (ci) => ci.controlId === editControlImplForm.controlId,
          )?.ciId) ||
        undefined;

      // One CI per SSP — use the known single CI rather than creating a new one
      if (!ciId) {
        ciId = singleCiId || undefined;
      }

      await bulkUpdateImplementedRequirements(selectedSSPId, ciId, payload);

      // Save SSP-level parameter overrides via the scoped endpoint (existing IR only)
      if (editingControlImplId && editControlImplForm.setParameters.length > 0) {
        const paramPayload = editControlImplForm.setParameters
          .filter((p) => p.paramId.trim())
          .map((p) => ({
            "param-id": p.paramId,
            values: p.values.split(",").map((v) => v.trim()).filter(Boolean),
          }));
        await putSspIrSetParameters(selectedSSPId, editingControlImplId, paramPayload);
      }

      setEditingControlImplId(null);
      setEditingCiId(null);
      setShowEditControlImpl(false);
      useToastStore
        .getState()
        .showSuccess("Success", "Control implementation saved");
      await fetchControlImplementations(
        selectedSSPId,
        controlIdFilter || undefined,
      );
      if (selectedSSPId) fetchBaselineControls(selectedSSPId);
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      useToastStore
        .getState()
        .showError(
          "Error",
          detail ?? (err instanceof Error ? err.message : "Failed to save control implementation"),
        );
    } finally {
      setSavingControlImpl(false);
    }
  };

  const handleDeleteControlImpl = async (ciId: string) => {
    if (!selectedSSPId) return;
    const confirmed = window.confirm(
      "Delete this control implementation?\n\nThis also removes its implemented requirements and cannot be undone.",
    );
    if (!confirmed) return;
    setDeletingControlImplId(ciId);
    try {
      await deleteSspControlImplementation(selectedSSPId, ciId);
      useToastStore
        .getState()
        .showSuccess("Success", "Control implementation deleted");
      await fetchControlImplementations(
        selectedSSPId,
        controlIdFilter || undefined,
      );
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      setError(detail ?? (err instanceof Error ? err.message : "Failed to delete control implementation"));
    } finally {
      setDeletingControlImplId(null);
    }
  };

  // ── Lifecycle transitions ─────────────────────────────────
  const handleLifecycleTransition = async () => {
    if (!sspDetail?.documentId || !transitionTarget) return;
    setTransitioningLifecycle(true);
    try {
      await transitionDocument(sspDetail.documentId, {
        targetStatus: transitionTarget,
        comments: transitionComment || undefined,
      });
      await fetchLifecycle(sspDetail.documentId);
      setShowTransitionModal(false);
      setTransitionTarget("");
      setTransitionComment("");
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `Status changed to "${transitionTarget.replace(/_/g, " ")}"`,
        );
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      useToastStore
        .getState()
        .showError(
          "Error",
          detail ?? (err instanceof Error ? err.message : "Failed to transition lifecycle"),
        );
    } finally {
      setTransitioningLifecycle(false);
    }
  };

  // ── OSCAL Export (real API) ───────────────────────────────
  // ── SSP Export (generic export API) ──────────────────────
  const handleExport = async (format: "json" | "xml" | "yaml") => {
    const docId = exportTargetDocId || sspDetail?.documentId;
    if (!docId) return;
    setExportingDoc(true);
    setExportFormat(format);
    setShowExport(false);
    try {
      const sspName = sspDetail?.title || "ssp";
      const res = await exportCatalogDoc(docId, format);
      let job = res.data;
      const getJobId = (j: typeof job) => j.jobId || j.id || "";
      if (job.status === "pending") {
        for (let i = 0; i < 30; i++) {
          await new Promise((r) => setTimeout(r, 2000));
          const poll = await getExportJob(getJobId(job));
          job = poll.data;
          if (job.status !== "pending") break;
        }
      }
      if (job.status !== "completed") {
        useToastStore
          .getState()
          .showError(
            "Error",
            job.status === "failed"
              ? "Export failed"
              : "Export timed out - please try again",
          );
        return;
      }
      const downloadUrl =
        job.downloadUrl || getExportDownloadUrl(getJobId(job));
      const downloadRes = await downloadFile(downloadUrl);
      if (!downloadRes) throw new Error("No response from download");

      // Extract documentTitle from response headers
      let documentTitle = sspName;
      const xDocumentTitle = downloadRes.headers?.["x-document-title"];
      if (xDocumentTitle) {
        documentTitle = xDocumentTitle;
      } else {
        // Try to extract from Content-Disposition header
        const contentDisposition =
          downloadRes.headers?.["content-disposition"] || "";
        const filenameMatch = contentDisposition.match(
          /filename\*?=(?:"([^"]*)"|([^;,\s]*))/,
        );
        if (filenameMatch && (filenameMatch[1] || filenameMatch[2])) {
          const filename = (filenameMatch[1] || filenameMatch[2]).split(".")[0];
          documentTitle = filename;
        }
      }

      // Get MIME type from API response Content-Type header first
      let mimeType = downloadRes.headers?.["content-type"] || "";
      if (!mimeType) {
        // Fallback: use selected format
        const mimeTypes: Record<string, string> = {
          json: "application/json",
          xml: "application/xml",
          yaml: "application/yaml",
        };
        mimeType = mimeTypes[format] || "application/octet-stream";
      }
      const blob = new Blob([downloadRes.data], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${documentTitle}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      useToastStore
        .getState()
        .showSuccess("Success", "Export ready - download started");
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Export failed",
        );
    } finally {
      setExportingDoc(false);
      setExportTargetDocId(null);
    }
  };

  // ── SSP OSCAL Import handlers ──────────────────────────
  const sspStartValidation = async (file: File) => {
    setSspValidating(true);
    setSspImportError(null);
    setSspValidateResult(null);
    try {
      const res = await validateImport(file);
      if (
        !["system-security-plan", "ssp"].includes(
          res.data.documentType.toLowerCase(),
        )
      ) {
        const msg = `Expected a system-security-plan but got "${res.data.documentType}"`;
        setSspImportError(msg);
        useToastStore.getState().showError("Error", msg);
      } else {
        setSspValidateResult(res.data);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Validation failed";
      setSspImportError(msg);
      useToastStore.getState().showError("Error", msg);
    } finally {
      setSspValidating(false);
    }
  };

  const sspIsAllowedUrl = (url: string): boolean =>
    /\.(json|yaml|yml|xml)(\?.*)?$/i.test(url.split("#")[0]);

  const sspToRawUrl = (url: string): string => {
    const ghMatch = url.match(
      /^https?:\/\/github\.com\/([^/]+\/[^/]+)\/blob\/(.+)$/,
    );
    if (ghMatch)
      return `https://raw.githubusercontent.com/${ghMatch[1]}/${ghMatch[2]}`;
    return url;
  };

  const sspFetchUrl = async () => {
    if (!sspImportUrl.trim()) return;
    if (!sspIsAllowedUrl(sspImportUrl.trim())) {
      const msg = "URL must point to a .json, .yaml, or .xml file";
      setSspImportError(msg);
      useToastStore.getState().showError("Error", msg);
      return;
    }
    setSspValidating(true);
    setSspImportError(null);
    setSspValidateResult(null);
    setSspImportFile(null);
    try {
      const rawUrl = sspToRawUrl(sspImportUrl.trim());
      const response = await fetch(rawUrl);
      if (!response.ok)
        throw new Error(
          `Failed to fetch: ${response.status} ${response.statusText}`,
        );
      const blob = await response.blob();
      const filename = sspImportUrl.split("/").pop() || "document";
      const file = new File([blob], filename, {
        type: blob.type || "application/octet-stream",
      });
      setSspImportFile(file);
      const res = await validateImport(file);
      if (
        !["system-security-plan", "ssp"].includes(
          res.data.documentType.toLowerCase(),
        )
      ) {
        const msg = `Expected a system-security-plan but got "${res.data.documentType}"`;
        setSspImportError(msg);
        useToastStore.getState().showError("Error", msg);
      } else {
        setSspValidateResult(res.data);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to fetch";
      setSspImportError(msg);
      useToastStore.getState().showError("Error", msg);
    } finally {
      setSspValidating(false);
    }
  };

  const sspRunImport = async () => {
    if (!sspImportFile) return;
    setSspImportStep("importing");
    setSspImportError(null);
    try {
      const res = await importSSP(
        sspImportFile,
        sspImportMode,
        importOrgId || undefined,
      );
      setSspImportResult(res.data);
      setShowSspImport(false);
      sspOpenSessionModal(res.data.id);
    } catch (err: unknown) {
      const title =
        err instanceof Error && err.name !== "Error"
          ? err.name
          : "Import Failed";
      const msg =
        err instanceof Error ? err.message : "This file already exists";
      setSspImportErrorTitle(title);
      setSspImportError(msg);
      setSspImportStep("findings");
      useToastStore.getState().showError(title, msg);
    }
  };

  const sspOpenSessionModal = async (sessionId: string) => {
    setShowSspSession(true);
    setSspSessionData(null);
    setSspSessionError(null);
    setSspSessionLoading(true);
    setSspCommitting(false);
    setSspAborting(false);
    try {
      const res = await getImportSession(sessionId);
      setSspSessionData(res.data);
    } catch (err: unknown) {
      setSspSessionError(
        err instanceof Error ? err.message : "Failed to fetch session",
      );
    } finally {
      setSspSessionLoading(false);
    }
  };

  useEffect(() => {
    if (!showSspSession || !sspSessionData) return;
    const status = sspSessionData.jobStatus.toLowerCase();
    if (status === "validated" || status === "failed" || status === "committed")
      return;
    const timer = setInterval(async () => {
      try {
        const res = await getImportSession(sspSessionData.id);
        setSspSessionData(res.data);
      } catch {
        /* silently ignore */
      }
    }, 10000);
    return () => clearInterval(timer);
  }, [showSspSession, sspSessionData]);

  const handleSspCommitSession = async () => {
    if (!sspSessionData) return;
    setSspCommitting(true);
    setSspSessionError(null);
    try {
      await commitImportSession(sspSessionData.id, importOrgId || undefined);
      fetchSSPList();
      useToastStore
        .getState()
        .showSuccess("Success", "SSP Imported Successfully");
      setShowSspSession(false);
      setSspSessionData(null);
      resetSspImport();
    } catch (err: unknown) {
      setSspSessionError(err instanceof Error ? err.message : "Commit failed");
    } finally {
      setSspCommitting(false);
    }
  };

  const handleSspAbortSession = async () => {
    if (!sspSessionData) return;
    setSspAborting(true);
    setSspSessionError(null);
    try {
      await abortImportSession(sspSessionData.id);
      setShowSspSession(false);
      setSspSessionData(null);
      resetSspImport();
      fetchSSPList();
    } catch (err: unknown) {
      setSspSessionError(err instanceof Error ? err.message : "Abort failed");
    } finally {
      setSspAborting(false);
    }
  };

  const resetSspImport = () => {
    setSspImportStep("select");
    setSspImportFile(null);
    setSspImportUrl("");
    setSspImportMode("full");
    setSspValidateResult(null);
    setSspValidating(false);
    setSspImportError(null);
    setSspImportErrorTitle(null);
    setSspImportResult(null);
    setImportOrgId("");
  };

  // ── CDEF Import ───────────────────────────────────────────
  const handleOpenImport = async () => {
    if (!selectedSSPId) return;
    setShowImportComponent(true);
    setCdefLoading(true);
    setCdefComponents([]);
    setSelectedImportComponentIds(new Set());
    setImportedComponentIds(new Set());
    try {
      const res = await getSspProfileMatchedComponents(selectedSSPId);
      setCdefComponents(
        res.data.map((comp) => ({
          ...comp,
          cdefTitle:
            (comp as any).cdefTitle ||
            (comp as any).definitionTitle ||
            comp.title,
        })),
      );
    } catch {
      setCdefComponents([]);
    } finally {
      setCdefLoading(false);
    }
  };

  const handleImportComponents = async (sourceComponentIds: string[]) => {
    if (!selectedSSPId || sourceComponentIds.length === 0) return;
    setImportingComponents(true);
    try {
      const importRes = await importComponentToSsp(
        selectedSSPId,
        sourceComponentIds,
      );
      await fetchComponents(selectedSSPId);
      setImportedComponentIds((prev) => {
        const next = new Set(prev);
        sourceComponentIds.forEach((id) => next.add(id));
        return next;
      });

      const importedComponents: ImportComponentsResponse =
        importRes.data?.components || importRes.data || [];
      const compTitle =
        importedComponents.length === 1
          ? importedComponents[0]?.title || "Imported Component"
          : `${importedComponents.length} Imported Components`;

      // Reset stale state and show popup
      setPostImportSelectedIdx(null);
      setPostImportSearch("");
      setSavedIrIds(new Set());
      setExcludedIrIds(new Set());
      setPostImportComponentTitle(compTitle);
      setShowPostImportPopup(true);
      setPostImportLoading(true);

      // Build the post-import control list from the import response.
      // Some API responses return imported controls in
      // sourceControlImplementations while controlImplementations is empty.
      const allIRs: {
        ciId: string;
        componentId: string;
        componentTitle: string;
        ir: ImplementedRequirementDetailResponse;
      }[] = [];

      for (const importedComp of importedComponents) {
        const componentTitle = importedComp?.title || "Imported Component";
        const importedControlGroups =
          importedComp.controlImplementations &&
            importedComp.controlImplementations.length > 0
            ? importedComp.controlImplementations
            : (importedComp.sourceControlImplementations || []).map((ci) => ({
              id: ci.id,
              implementedRequirements: (ci.implementedRequirements || []).map(
                (ir) => ({
                  id: ir.id,
                  controlId: ir.controlId,
                  implementationStatus: "planned",
                  origination: "system-specific",
                  description: "",
                  statements: [],
                  setParameters: [],
                  sortOrder: 0,
                  createdAt: "",
                  updatedAt: "",
                }),
              ),
            }));

        for (const ci of importedControlGroups) {
          for (const ir of ci.implementedRequirements || []) {
            allIRs.push({
              ciId: ci.id,
              componentId: importedComp.id,
              componentTitle,
              ir: {
                id: ir.id,
                controlId: ir.controlId,
                implementationStatus:
                  (ir as any).implementationStatus || "planned",
                origination: (ir as any).origination || "system-specific",
                description: ir.description || "",
                statements: (ir as any).statements || [],
                setParameters: (ir as any).setParameters || [],
                sortOrder: (ir as any).sortOrder || 0,
                createdAt: (ir as any).createdAt || "",
                updatedAt: (ir as any).updatedAt || "",
              } as ImplementedRequirementDetailResponse,
            });
          }
        }
      }

      // Build form state
      const statuses: Record<string, ImplementationStatus> = {};
      const descriptions: Record<string, string> = {};
      const stmtEdits: Record<
        string,
        { statementId: string; description: string; sortOrder: number }[]
      > = {};
      const paramEdits: Record<string, { paramId: string; values: string }[]> =
        {};

      allIRs.forEach(({ ir }) => {
        statuses[ir.id] =
          (ir.implementationStatus as ImplementationStatus) || "planned";
        descriptions[ir.id] = ir.description || "";
        stmtEdits[ir.id] = (ir.statements || []).map((s) => ({
          statementId: s.statementId,
          description: s.description || "",
          sortOrder: s.sortOrder,
        }));
        if (ir.setParameters && ir.setParameters.length > 0) {
          paramEdits[ir.id] = ir.setParameters.map((p) => ({
            paramId: p["param-id"],
            values: p.values.join(", "),
          }));
        }
      });

      setPostImportIRs(allIRs);
      setExcludedIrIds(new Set());
      setPostImportStatuses(statuses);
      setPostImportDescriptions(descriptions);
      setPostImportStmtEdits(stmtEdits);
      setPostImportControlDetails({});
      setPostImportParamEdits(paramEdits);
      setPostImportLoading(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to import components",
      );
      setPostImportLoading(false);
      setShowPostImportPopup(false);
    } finally {
      setImportingComponents(false);
    }
  };

  // Save a single IR's status, statements, and params
  const handleSaveSingleIR = async (
    ciId: string,
    ir: ImplementedRequirementDetailResponse,
  ) => {
    if (!selectedSSPId) return;
    setSavingIrId(ir.id);
    try {
      const newStatus = postImportStatuses[ir.id];
      const newStmts = postImportStmtEdits[ir.id];
      const newParams = postImportParamEdits[ir.id];

      const setParamsPayload = newParams
        ?.filter((p) => p.values.trim())
        .map((p) => ({
          "param-id": p.paramId,
          values: p.values
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean),
        }));

      await updateImplementedRequirement(selectedSSPId, ciId, {
        controlId: ir.controlId,
        implementationStatus:
          newStatus ||
          (ir.implementationStatus as ImplementationStatus) ||
          "planned",
        origination:
          (ir.origination as ControlOrigination) || "system-specific",
        description: postImportDescriptions[ir.id]?.trim() || undefined,
        ...(newStmts && newStmts.length > 0 ? { statements: newStmts } : {}),
        ...(setParamsPayload && setParamsPayload.length > 0
          ? { setParameters: setParamsPayload }
          : {}),
      });

      setSavedIrIds((prev) => new Set(prev).add(ir.id));

      // Refresh control implementations in background
      const ciRes = await listSspControlImplementations(selectedSSPId);
      setControlImpls(ciRes.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update control");
    } finally {
      setSavingIrId(null);
    }
  };

  // Bulk save all IRs from post-import
  const [bulkSaving, setBulkSaving] = useState(false);
  const handleBulkSaveAll = async () => {
    const includedIRs = postImportIRs.filter(
      ({ ir }) => !excludedIrIds.has(ir.id),
    );
    if (!selectedSSPId || includedIRs.length === 0) return;
    setBulkSaving(true);
    try {
      // Group IRs by ciId
      const grouped: Record<
        string,
        { ciId: string; irs: typeof postImportIRs }
      > = {};
      for (const item of includedIRs) {
        if (!grouped[item.ciId])
          grouped[item.ciId] = { ciId: item.ciId, irs: [] };
        grouped[item.ciId].irs.push(item);
      }

      for (const [ciId, group] of Object.entries(grouped)) {
        const payload: BulkImplementedRequirementPayload[] = group.irs.map(
          ({ ir }, idx) => {
            const newStatus = postImportStatuses[ir.id];
            const newStmts = postImportStmtEdits[ir.id];
            const newParams = postImportParamEdits[ir.id];
            return {
              controlId: ir.controlId,
              implementationStatus:
                newStatus ||
                (ir.implementationStatus as ImplementationStatus) ||
                "planned",
              origination:
                (ir.origination as ControlOrigination) || "system-specific",
              description: postImportDescriptions[ir.id]?.trim() || undefined,
              setParameters:
                newParams
                  ?.filter((p) => p.values.trim())
                  .map((p) => ({
                    "param-id": p.paramId,
                    values: p.values
                      .split(",")
                      .map((v) => v.trim())
                      .filter(Boolean),
                  })) || [],
              statements: newStmts?.length
                ? newStmts.map((s) => ({
                  statementId: s.statementId,
                  description: s.description || undefined,
                  sortOrder: s.sortOrder,
                }))
                : [],
              sortOrder: idx,
            };
          },
        );
        await bulkUpdateImplementedRequirements(selectedSSPId, ciId, payload);
      }

      setSavedIrIds(new Set(includedIRs.map(({ ir }) => ir.id)));
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `${includedIRs.length} controls saved successfully`,
        );
      // Refresh control implementations
      await fetchControlImplementations(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to bulk save controls",
      );
    } finally {
      setBulkSaving(false);
    }
  };

  // Close both import and post-import popups
  const closePostImportPopups = () => {
    setShowPostImportPopup(false);
    setShowImportComponent(false);
    setSelectedImportComponentIds(new Set());
    setPostImportIRs([]);
    setPostImportStatuses({});
    setPostImportDescriptions({});
    setPostImportStmtEdits({});
    setPostImportControlDetails({});
    setPostImportParamEdits({});
    setSavedIrIds(new Set());
    setExcludedIrIds(new Set());
    setPostImportLoadingControlId(null);
    setPostImportSelectedIdx(null);
    setPostImportSearch("");
  };

  const handleSelectPostImportControl = async (index: number) => {
    setPostImportSelectedIdx(index);
    const selected = postImportIRs[index];
    if (!selected || !ciCatalogId) return;

    const { ir } = selected;
    if (postImportControlDetails[ir.controlId]) return;

    setPostImportLoadingControlId(ir.controlId);
    try {
      const res = await getControl(ciCatalogId, ir.controlId);
      setPostImportControlDetails((prev) => ({
        ...prev,
        [ir.controlId]: res.data,
      }));
      setPostImportParamEdits((prev) => {
        if ((prev[ir.id] || []).length > 0) return prev;
        if (!res.data.params?.length) return prev;
        return {
          ...prev,
          [ir.id]: res.data.params.map((param) => ({
            paramId: param.paramId,
            values: "",
          })),
        };
      });
      setPostImportStmtEdits((prev) => {
        if ((prev[ir.id] || []).length > 0) return prev;
        let sortOrder = 0;
        const statements = res.data.parts
          .filter((part) => part.name === "statement")
          .flatMap((part) => [
            {
              statementId: part.partId,
              description: part.prose || "",
              sortOrder: sortOrder++,
            },
            ...part.parts.map((sub) => ({
              statementId: sub.partId,
              description: sub.prose || "",
              sortOrder: sortOrder++,
            })),
          ]);
        return statements.length > 0 ? { ...prev, [ir.id]: statements } : prev;
      });
    } catch {
      // Keep the control selectable even if detail fetch fails.
    } finally {
      setPostImportLoadingControlId((current) =>
        current === ir.controlId ? null : current,
      );
    }
  };

  // ── Components: filter + group by Group Name ─────────────
  const componentGroups = useMemo(() => {
    let filtered = sspComponents;
    if (componentSearchFilter) {
      const q = componentSearchFilter.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          (c.description ?? "").toLowerCase().includes(q),
      );
    }
    if (componentTypeFilter) {
      filtered = filtered.filter(
        (c) => c.componentType === componentTypeFilter,
      );
    }
    if (componentGroupFilter) {
      filtered = filtered.filter((c) => {
        const gn = c.props?.find((p) => p.name === "Group Name")?.value ?? "";
        return gn === componentGroupFilter;
      });
    }
    // Group by "Group Name" prop
    const groups: Record<string, typeof filtered> = {};
    for (const comp of filtered) {
      const gn =
        comp.props?.find((p) => p.name === "Group Name")?.value || "Ungrouped";
      if (!groups[gn]) groups[gn] = [];
      groups[gn].push(comp);
    }
    return groups;
  }, [
    sspComponents,
    componentSearchFilter,
    componentTypeFilter,
    componentGroupFilter,
  ]);

  const availableGroupNames = useMemo(() => {
    const names = new Set<string>();
    for (const c of sspComponents) {
      const gn = c.props?.find((p) => p.name === "Group Name")?.value;
      if (gn) names.add(gn);
    }
    return Array.from(names).sort();
  }, [sspComponents]);

  const availableComponentTypes = useMemo(() => {
    const types = new Set<string>();
    for (const c of sspComponents) {
      if (c.componentType) types.add(c.componentType);
    }
    return Array.from(types).sort();
  }, [sspComponents]);

  // ── Lifecycle derived state ───────────────────────────────
  const lifecycle = getSelectedSspLifecycle();
  const isPublishedSsp = lifecycle === "published";

  useEffect(() => {
    if (!isPublishedSsp) return;
    setPendingDelete(null);
    setShowImportComponent(false);
    setShowAddInfoType(false);
    setShowAddComponent(false);
    setShowAddAsset(false);
    setShowAddLevAuth(false);
    setShowAddSystemUser(false);
    setShowAddInterconnection(false);
    setShowAddRole(false);
    setShowAddParty(false);
    setShowAssignRP(false);
    setShowAddLocation(false);
    setShowAddControlImpl(false);
    setShowEditControlImpl(false);
    setEditingSysChars(false);
    setEditingLevAuthId(null);
    setEditingSystemUserId(null);
    setEditingInterconnectionId(null);
    setEditingRoleId(null);
    setEditingPartyId(null);
    setEditingLocationId(null);
    setEditingControlImplId(null);
    setEditingSspTitle(false);
  }, [isPublishedSsp]);

  // ── SSP title (document) rename — allowed only before publish ──
  const currentSspDocumentId =
    sspList.find((s) => s.id === selectedSSPId)?.documentId ?? "";
  const currentSspTitle =
    (currentSspDocumentId ? sspDocTitles[currentSspDocumentId] : "") ||
    sspDetail?.title ||
    "";
  const handleRenameSsp = async () => {
    const newTitle = sspTitleDraft.trim();
    if (!currentSspDocumentId || !newTitle || newTitle === currentSspTitle) {
      setEditingSspTitle(false);
      return;
    }
    setSavingSspTitle(true);
    try {
      await renameDocument(currentSspDocumentId, newTitle);
      setSspDocTitles((prev) => ({ ...prev, [currentSspDocumentId]: newTitle }));
      setSspDetail((prev) => (prev ? { ...prev, title: newTitle } : prev));
      useToastStore.getState().showSuccess("Success", "SSP title updated");
      setEditingSspTitle(false);
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      setError(
        detail ?? (err instanceof Error ? err.message : "Failed to rename SSP"),
      );
    } finally {
      setSavingSspTitle(false);
    }
  };

  const currentStageIdx = LIFECYCLE_STAGES.findIndex(
    (s) => s.key === lifecycle,
  );

  // Lifecycle action buttons per status (matching Profiles pattern)
  const lifecycleActions: Record<
    string,
    { label: string; target: string; color: string; icon: React.ReactNode }[]
  > = {
    draft: [
      {
        label: "Submit for Review",
        target: "in_review",
        color: "bg-blue-600 hover:bg-blue-700 shadow-blue-500/20",
        icon: <Send className="size-3.5" />,
      },
    ],
    in_review: [
      {
        label: "Approve",
        target: "approved",
        color: "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/20",
        icon: <ShieldCheck className="size-3.5" />,
      },
      {
        label: "Reject",
        target: "draft",
        color: "bg-red-600 hover:bg-red-700 shadow-red-500/20",
        icon: <XCircle className="size-3.5" />,
      },
    ],
    approved: [
      {
        label: "Publish",
        target: "published",
        color: "bg-purple-600 hover:bg-purple-700 shadow-purple-500/20",
        icon: <Globe className="size-3.5" />,
      },
    ],
    published: [
      {
        label: "Supersede",
        target: "superseded",
        color: "bg-orange-600 hover:bg-orange-700 shadow-orange-500/20",
        icon: <Archive className="size-3.5" />,
      },
      {
        label: "Withdraw",
        target: "withdrawn",
        color: "bg-rose-700 hover:bg-rose-800 shadow-rose-500/20",
        icon: <Ban className="size-3.5" />,
      },
    ],
    rejected: [
      {
        label: "Submit for Review",
        target: "in_review",
        color: "bg-blue-600 hover:bg-blue-700 shadow-blue-500/20",
        icon: <Send className="size-3.5" />,
      },
    ],
  };

  // Get lifecycle actions for current state (permission-based filtering is done at render time)
  const hasUpdatePermission = useRBACPermission("ssp", "update");
  const hasApprovePermission = useRBACPermission("ssp", "approve");
  const hasPublishPermission = useRBACPermission("ssp", "publish");
  const currentLifecycleActions = (lifecycleActions[lifecycle] ?? []).filter((a) => {
    // Show "Submit for Review" only if user has update permission
    if (a.label === "Submit for Review") return hasUpdatePermission;
    // Show "Approve" and "Reject" only if user has approve permission
    if (["Approve", "Reject"].includes(a.label)) return hasApprovePermission;
    // Show "Publish" only if user has publish permission
    if (a.label === "Publish") return hasPublishPermission;
    // Show "Supersede" and "Withdraw" only if user has update permission
    if (["Supersede", "Withdraw"].includes(a.label)) return hasUpdatePermission;
    return true;
  });

  const openTransitionModal = (target: string) => {
    setTransitionTarget(target);
    setTransitionComment("");
    setShowTransitionModal(true);
  };

  // ══════════════════════════════════════════════════════════
  // LOADING SKELETON (when navigating via URL)
  // ══════════════════════════════════════════════════════════
  if (sspIdFromUrl && !selectedSSPId && detailLoading) {
    const LoadingSpinner = ({ text = "Loading" }: { text?: string }) => (
      <div className="flex flex-col items-center gap-3 p-8">
        <Loader2 className="size-8 text-muted-foreground animate-spin" />
        <p className="text-sm text-muted-foreground">{text}</p>
      </div>
    );
    return <LoadingSpinner text="Loading SSP details" />;
  }

  // ══════════════════════════════════════════════════════════
  // LIST VIEW
  // ══════════════════════════════════════════════════════════
  if (!selectedSSPId && !sspIdFromUrl) {
    return (
      <div className="h-full min-h-0 flex flex-col gap-6">
        <div className="flex-shrink-0 flex items-center justify-between gap-4">
          <div>
            <h1>System Security Plan</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Document and manage the implementation of security controls across
              the system.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <RBACAction module="ssp" action="create">
              <button
                onClick={() => {
                  resetSspImport();
                  setShowSspImport(true);
                  fetchAccessibleOrgs("ssp", "create");
                }}
                className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-indigo-500 dark:border-indigo-400 text-sm font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-all hover:-translate-y-px"
              >
                <Import className="size-4" /> Import SSP
              </button>
              <button
                onClick={() => {
                  fetchProfiles();
                  fetchAccessibleOrgs("ssp", "create");
                  // Full reset on every "New SSP" click so the modal opens
                  // fresh — title, profile, org, type and downstream picks
                  // never leak from a previous attempt.
                  setSspForm({
                    title: "",
                    shortCode: "",
                    importProfileHref: "",
                    owningOrgUnitId: "",
                  });
                  setSspInheritanceType("");
                  setSspSelectedAppId("");
                  setSspSelectedServiceIds([]);
                  setInheritedIds([]);
                  // Force a fresh inheritance fetch so any SSP created in
                  // a prior modal session shows up with isUsed=true.
                  setSspInheritanceRefreshTick((t) => t + 1);
                  setShowNewSSP(true);
                }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-all shadow-[0_4px_14px_rgba(79,70,229,0.3)] dark:shadow-[0_4px_14px_rgba(139,92,246,0.3)] hover:-translate-y-px"
              >
                <Plus className="size-4" />
                New SSP
              </button>
            </RBACAction>
          </div>
        </div>

        {/* Error banner */}
        {listError && (
          <div className="flex-shrink-0 flex items-center gap-3 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl text-sm text-red-700 dark:text-red-400">
            <AlertCircle className="size-4 flex-shrink-0" />
            <span>{listError}</span>
            <button
              onClick={fetchSSPList}
              className="ml-auto text-xs font-bold underline"
            >
              Retry
            </button>
          </div>
        )}

        {/* Search + filters + Sort + View toggle.
            Rendered whenever the list has ever loaded — hiding it on an empty
            result would trap the user with no way to clear their own search. */}
        {!listLoading && !creating && (
          <div className="flex-shrink-0 flex flex-wrap items-center gap-3">
            <TableToolbar
              search={{
                value: sspSearchQuery,
                onChange: handleSspSearchChange,
                onSubmit: submitSspSearch,
                placeholder:
                  "Search SSPs by name, source, profile or document ID",
                ariaLabel: "Search SSPs",
              }}
              filters={[
                {
                  key: "status",
                  value: sspStatusFilter,
                  onChange: (v) => {
                    setSspStatusFilter(v);
                    setSspCurrentPage(0);
                  },
                  options: [
                    { value: "", label: "All Status" },
                    { value: "draft", label: "Draft", dot: "bg-slate-400" },
                    { value: "in_review", label: "In Review", dot: "bg-blue-500" },
                    { value: "approved", label: "Approved", dot: "bg-emerald-500" },
                    { value: "published", label: "Published", dot: "bg-emerald-500" },
                    { value: "rejected", label: "Rejected", dot: "bg-red-500" },
                    { value: "superseded", label: "Superseded", dot: "bg-orange-500" },
                    { value: "withdrawn", label: "Withdrawn", dot: "bg-rose-700" },
                  ],
                },
              ]}
              // TableToolbar speaks "card" | "table"; this page's own state
              // has always called the table view "list".
              viewMode={sspViewMode === "list" ? "table" : "card"}
              onViewModeChange={(mode) => {
                setSspViewMode(mode === "table" ? "list" : "card");
                setSspCurrentPage(0);
              }}
              pageSize={sspPageSize}
              onPageSizeChange={(n) => {
                setSspPageSize(n);
                setSspCurrentPage(0);
              }}
              pageSizeOptions={
                sspViewMode === "list" ? [10, 25, 50, 100] : [12, 24, 48, 96]
              }
              pageSizeLabel={
                sspViewMode === "list" ? "Rows per page" : "SSPs per page"
              }
            />
          </div>
        )}

        {/* SSP — Card grid view (Catalog-style via EntityCardGrid) */}
        {sspViewMode === "card" && (
          <EntityCardGrid
            items={paginatedSspList}
            loading={listLoading || creating}
            getKey={(ssp) => (ssp as { id: string }).id}
            getStatus={(ssp) =>
              (sspLifecycles[(ssp as { documentId: string }).documentId] ||
                "draft") as LifecycleStatus
            }
            onOpen={(ssp) => {
              const id = (ssp as { id: string }).id;
              setSelectedSSPId(id);
              setActiveTab("System Characteristics");
            }}
            page={sspCurrentPage}
            pageSize={sspPageSize}
            totalItems={sspTotal}
            onPageChange={setSspCurrentPage}
            itemLabel="SSPs"
            renderHeaderRight={(ssp) => {
              const { isService, Icon } = getSspCardTheme(ssp);
              return (
                <Icon
                  className={`size-4 ${isService ? "text-violet-600 dark:text-violet-400" : "text-sky-600 dark:text-sky-400"}`}
                />
              );
            }}
            emptyIcon={<Shield className="size-8 text-[#cbd5e1] dark:text-white/30" />}
            emptyText="No System Security Plans found"
            renderTitle={(ssp) => {
              const r = ssp as { title?: string; documentId: string };
              const title =
                r.title || sspDocTitles[r.documentId] || r.documentId;
              return (
                <TitleTooltip text={title}>
                  <h3 className="text-[13px] font-bold leading-snug tracking-[-0.01em] text-foreground truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                    {title}
                  </h3>
                </TitleTooltip>
              );
            }}
            renderBadges={(ssp) => {
              const sourceType = (ssp as { sourceType?: string }).sourceType;
              if (!sourceType) return null;
              const { isService, Icon } = getSspCardTheme(ssp);
              return (
                <span
                  className={
                    isService
                      ? "inline-flex items-center gap-1 whitespace-nowrap px-1.5 py-0.5 rounded-md text-[10px] font-bold tracking-wider border capitalize bg-violet-50 text-violet-700 border-violet-200/70 dark:bg-violet-500/10 dark:text-violet-300 dark:border-violet-500/20"
                      : "inline-flex items-center gap-1 whitespace-nowrap px-1.5 py-0.5 rounded-md text-[10px] font-bold tracking-wider border capitalize bg-sky-50 text-sky-700 border-sky-200/70 dark:bg-sky-500/10 dark:text-sky-300 dark:border-sky-500/20"
                  }
                >
                  <Icon className="size-3" />
                  {sourceType.toLowerCase()}
                </span>
              );
            }}
            renderStats={(ssp) => {
              const r = ssp as {
                sourceType?: string;
                sourceName?: string;
                profileName?: string;
              };
              const sourceDisplay = r.sourceName || "—";
              const profileDisplay = r.profileName || "—";
              const { isService, Icon } = getSspCardTheme(ssp);
              return (
                <StatTiles>
                  <StatTile
                    icon={<Icon className="size-3.5" />}
                    accent={isService ? "violet" : "sky"}
                    value={sourceDisplay}
                    label="Source"
                    tooltip={sourceDisplay}
                  />
                  <div className="flex flex-1 items-center justify-center px-2.5 py-2 min-w-0">
                    {r.profileName ? (
                      <Tip
                        label={profileDisplay}
                        className="flex min-w-0 max-w-full"
                        contentClassName="max-w-[200px] whitespace-normal break-words text-left leading-snug px-2 py-1 text-[10px]"
                      >
                        <span className="flex items-center px-2 py-1 rounded-md bg-violet-50 dark:bg-violet-500/10 ring-1 ring-violet-200 dark:ring-violet-500/20 text-violet-700 dark:text-violet-300 text-[12px] font-semibold min-w-0 max-w-full">
                          <span className="truncate">{r.profileName}</span>
                        </span>
                      </Tip>
                    ) : (
                      <span className="text-[12px] text-muted-foreground">—</span>
                    )}
                  </div>
                </StatTiles>
              );
            }}
            renderDates={(ssp) => {
              const r = ssp as { createdAt?: string; updatedAt?: string };
              const created = r.createdAt
                ? new Date(r.createdAt).toLocaleDateString("en-GB")
                : "—";
              const updated = new Date(
                r.updatedAt || r.createdAt || Date.now(),
              ).toLocaleDateString("en-GB");
              return <MetaDates created={created} updated={updated} />;
            }}
            renderFooterActions={(ssp) => {
              const rowLifecycle = getSspSummaryLifecycle(
                ssp as SspSummaryResponse,
              );
              return (
                <CardActions
                  deleteModule="ssp"
                  exportTooltip="Export SSP"
                  deleteTooltip="Delete SSP"
                  deleteDisabledTooltip="Only draft SSPs can be deleted"
                  canDelete={rowLifecycle !== "published"}
                  deleting={deleting}
                  onExport={() => {
                    setExportTargetDocId(
                      (ssp as { documentId: string }).documentId,
                    );
                    setShowExport(true);
                  }}
                  onDelete={() => setDeleteTarget(ssp as SspSummaryResponse)}
                />
              );
            }}
          />
        )}


        {/* SSP — Table (list) view */}
        {sspViewMode === "list" && (
          <DataTable
            columns={[
              {
                key: "sno",
                header: "#",
                align: "center",
                render: (_row, idx) => (
                  <span className="text-muted-foreground font-medium">
                    {sspRowSerial(idx)}
                  </span>
                ),
              },
              {
                key: "title",
                header: "SSP Name",
                sortable: true,
                align: "center",
                cellClassName: "text-left [&>div]:justify-start",
                ellipsis: 2,
                tooltip: (row) =>
                  (row as any).title ||
                  sspDocTitles[(row as any).documentId] ||
                  (row as any).documentId ||
                  "",
                render: (row) => (
                  <span className="font-medium">
                    {(row as any).title ||
                      sspDocTitles[(row as any).documentId] ||
                      (row as any).documentId}
                  </span>
                ),
              },
              {
                key: "sourceType",
                header: "Source Type",
                sortable: true,
                align: "center",
                render: (row) => {
                  const type = (row as any).sourceType as string | undefined;
                  if (!type)
                    return <span className="text-muted-foreground">-</span>;
                  return (
                    <span className="inline-flex items-center justify-center w-[110px] text-[10px] font-bold tracking-wide capitalize px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 ring-1 ring-indigo-200 dark:ring-indigo-500/20">
                      {type.toLowerCase()}
                    </span>
                  );
                },
              },
              {
                key: "sourceName",
                header: "Source Name",
                sortable: true,
                align: "center",
                cellClassName: "text-left [&>div]:justify-start",
                render: (row) => {
                  const name = (row as any).sourceName as string | undefined;
                  return name ? (
                    <span className="text-xs font-medium">{name}</span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  );
                },
              },
              {
                key: "profile",
                header: "Profile",
                sortable: true,
                align: "center",
                cellClassName: "text-left [&>div]:justify-start",
                render: (row) =>
                  (row as any).profileName ? (
                    <span className="text-foreground font-medium text-xs">
                      {(row as any).profileName}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  ),
              },
              {
                key: "status",
                header: "Status",
                sortable: true,
                align: "center",
                render: (row) => {
                  const lc = (sspLifecycles[(row as any).documentId] ||
                    "draft") as LifecycleStatus;
                  return <StatusBadge status={lc as Parameters<typeof StatusBadge>[0]["status"]} />;
                },
              },
              {
                key: "owningOrgUnitName",
                header: "Organization",
                sortable: true,
                align: "center",
                cellClassName: "text-left [&>div]:justify-start",
                render: (row) => {
                  const orgName = (row as any).owningOrgUnitName as string | undefined;
                  return orgName ? (
                    <span className="text-xs font-medium">{orgName}</span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  );
                },
              },
              {
                key: "created",
                header: "Created On",
                sortable: true,
                align: "center",
                render: (row) => (
                  <span className="text-muted-foreground">
                    {new Date((row as any).createdAt).toLocaleDateString("en-GB")}
                  </span>
                ),
              },
              {
                key: "updated",
                header: "Updated On",
                sortable: true,
                align: "center",
                render: (row) => (
                  <span className="text-muted-foreground">
                    {new Date(
                      (row as any).updatedAt || (row as any).createdAt,
                    ).toLocaleDateString("en-GB")}
                  </span>
                ),
              },
              {
                key: "action",
                header: "Action",
                align: "center",
                render: (row) => {
                  const rowLifecycle = getSspSummaryLifecycle(
                    row as SspSummaryResponse,
                  );
                  return (
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          setExportTargetDocId((row as any).documentId);
                          setShowExport(true);
                        }}
                        className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-indigo-600 transition-colors"
                        title="Export"
                      >
                        <SquareArrowOutUpRight className="size-4" />
                      </button>
                      <RBACAction module="ssp" action="delete">
                        <button
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            setDeleteTarget(row as any);
                          }}
                          disabled={deleting || rowLifecycle === "published"}
                          className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                          title={
                            rowLifecycle === "published"
                              ? "Only draft SSPs can be deleted"
                              : "Delete"
                          }
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </RBACAction>
                    </div>
                  );
                },
              },
            ]}
            data={paginatedSspList}
            rowKey={(r) => (r as any).id}
            onRowClick={(row) => {
              setSelectedSSPId((row as any).id);
              setActiveTab("System Characteristics");
            }}
            loading={listLoading || creating}
            emptyText="No System Security Plans found"
            page={sspCurrentPage}
            pageSize={sspPageSize}
            totalItems={sspTotal}
            onPageChange={setSspCurrentPage}
            itemLabel="SSPs"
            sortKey={sspSortKey}
            sortDir={sspSortDir}
            onSort={handleSspSort}
          />
        )}

        {/* New SSP Modal */}
        <NewSspDialog
          open={showNewSSP}
          onClose={() => setShowNewSSP(false)}
          sspForm={sspForm}
          setSspForm={setSspForm}
          sspInheritanceType={sspInheritanceType}
          setSspInheritanceType={setSspInheritanceType}
          sspSelectedAppId={sspSelectedAppId}
          setSspSelectedAppId={setSspSelectedAppId}
          sspSelectedServiceIds={sspSelectedServiceIds}
          setSspSelectedServiceIds={setSspSelectedServiceIds}
          setInheritedIds={setInheritedIds}
          creating={creating}
          loadingProfiles={loadingProfiles}
          profiles={profiles}
          accessibleOrgs={accessibleOrgs}
          accessibleOrgsLoading={accessibleOrgsLoading}
          sspInheritanceLoading={sspInheritanceLoading}
          sspInheritance={sspInheritance}
          sspApplicationOptions={sspApplicationOptions}
          sspServiceOptions={sspServiceOptions}
          sspTypeSelectionIncomplete={sspTypeSelectionIncomplete}
          setShowInheritancePicker={setShowInheritancePicker}
          handleCreateSSP={handleCreateSSP}
        />

        <InheritancePickerModal
          open={
            showInheritancePicker &&
            (sspInheritanceType === "application" ||
              sspInheritanceType === "service")
          }
          mode={sspInheritanceType === "service" ? "service" : "application"}
          selectedAppId={sspSelectedAppId}
          selectedServiceId={sspSelectedServiceIds[0] ?? ""}
          serviceProviderSsps={availableProviderSsps}
          onClose={() => {
            setShowInheritancePicker(false);
            setShowNewSSP(true);
          }}
          onConfirm={async (ids, meta) => {
            // Application-mode → POST /ssps/from-application
            // Service-mode     → POST /ssps/from-service
            // Resolve UUIDs from SSPEditor's own sspInheritance data as a
            // fallback in case the picker's parallel fetch hasn't settled.
            setInheritedIds(ids);
            setShowInheritancePicker(false);
            // sspSelectedAppId is now the applicationId UUID directly
            // (the dropdown was switched off appCode to avoid collisions).
            const resolvedAppId = meta.applicationId || sspSelectedAppId;
            // Service mode normally creates straight from NewSspDialog; this
            // picker path is the fallback, so honour every selected service.
            const resolvedServiceIds = meta.serviceId
              ? [meta.serviceId]
              : sspSelectedServiceIds;
            if (sspInheritanceType === "application" && resolvedAppId) {
              await handleCreateSSP({
                applicationId: resolvedAppId,
                componentIds: ids,
                providerSspIds: meta.providerSspIds ?? [],
              });
            } else if (
              sspInheritanceType === "service" &&
              resolvedServiceIds.length
            ) {
              await handleCreateSSP({
                serviceIds: resolvedServiceIds,
                componentIds: ids,
              });
            } else {
              await handleCreateSSP();
            }
          }}
          creating={creating}
        />

        {/* Delete Confirmation */}
        <DeleteConfirmDialog
          open={!!deleteTarget}
          type="SSP"
          name={
            deleteTarget
              ? sspDocTitles[deleteTarget.documentId] || deleteTarget.documentId
              : ""
          }
          deleting={deleting}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => handleDeleteSSP()}
        />

        {/* ── Import SSP Modal (list view) — same component as editor-view ── */}
        <ImportSspDialog
          open={showSspImport}
          onClose={() => {
            setShowSspImport(false);
            resetSspImport();
          }}
          resetSspImport={resetSspImport}
          sspImportStep={sspImportStep}
          setSspImportStep={setSspImportStep}
          sspImportFile={sspImportFile}
          setSspImportFile={setSspImportFile}
          sspImportUrl={sspImportUrl}
          setSspImportUrl={setSspImportUrl}
          sspImportMode={sspImportMode}
          setSspImportMode={setSspImportMode}
          sspValidateResult={sspValidateResult}
          setSspValidateResult={setSspValidateResult}
          sspValidating={sspValidating}
          setSspValidating={setSspValidating}
          sspImportError={sspImportError}
          setSspImportError={setSspImportError}
          sspImportErrorTitle={sspImportErrorTitle}
          sspRunImport={sspRunImport}
          sspStartValidation={sspStartValidation}
          sspFetchUrl={sspFetchUrl}
        />

        {/* ── Import Session Modal (list view) — same component as editor-view ── */}
        <ImportSessionDialog
          open={showSspSession}
          onClose={() => setShowSspSession(false)}
          sspSessionData={sspSessionData}
          setSspSessionData={setSspSessionData}
          sspSessionLoading={sspSessionLoading}
          sspSessionError={sspSessionError}
          sspCommitting={sspCommitting}
          sspAborting={sspAborting}
          handleSspCommitSession={handleSspCommitSession}
          handleSspAbortSession={handleSspAbortSession}
          resetSspImport={resetSspImport}
          onCommittedDone={() => {
            setShowSspSession(false);
            fetchSSPList();
          }}
        />

        {/* Export Modal (List View) — same component as editor-view Export */}
        <ExportDialog
          open={showExport}
          onClose={() => {
            setShowExport(false);
            setExportTargetDocId(null);
          }}
          exportingDoc={exportingDoc}
          handleExport={handleExport}
        />

        {/* Export Pulse Overlay (List View) */}
        {exportingDoc && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-4 bg-card rounded-2xl px-10 py-8 shadow-2xl">
              <div className="relative flex items-center justify-center">
                <div className="absolute size-16 rounded-full bg-indigo-500/20 dark:bg-indigo-500/20 animate-ping" />
                <div className="absolute size-12 rounded-full bg-indigo-500/30 dark:bg-indigo-500/30 animate-pulse" />
                <Upload className="relative size-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <p className="text-sm font-semibold text-foreground mt-2">
                Exporting as {exportFormat.toUpperCase()}
              </p>
              <p className="text-xs text-muted-foreground">
                Please wait while we prepare your file
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════
  // DETAIL VIEW
  // ══════════════════════════════════════════════════════════

  if (detailLoading) {
    return <LoadingSpinner text="Loading SSP details" />;
  }

  // Tab-specific add buttons
  const tabAddBtn: Partial<
    Record<TabId, { label: string; action: () => void }>
  > = {
    "Information Types": {
      label: "New Info Type",
      action: () => setShowAddInfoType(true),
    },
    "Leveraged Authorizations": {
      label: "New Authorization",
      action: () => setShowAddLevAuth(true),
    },
    "System Users": {
      label: "New User",
      action: () => setShowAddSystemUser(true),
    },
    Interconnections: {
      label: "New Interconnection",
      action: () => setShowAddInterconnection(true),
    },
    Locations: {
      label: "New Location",
      action: () => setShowAddLocation(true),
    },
  };
  const currentAddBtn = tabAddBtn[activeTab];

  // ── Generic tab filter + paginate helper ──
  function filterAndPaginate<T>(items: T[], searchFields: (keyof T)[]) {
    const q = tabSearch.toLowerCase();
    const filtered = q
      ? items.filter((item) =>
        searchFields.some((f) =>
          String(item[f] ?? "")
            .toLowerCase()
            .includes(q),
        ),
      )
      : items;
    const paginated = filtered.slice(
      (tabPage - 1) * tabPageSize,
      tabPage * tabPageSize,
    );
    return { filtered, paginated };
  }

  const { filtered: filtInfoTypes, paginated: pagInfoTypes } =
    filterAndPaginate(infoTypes, ["title" as keyof InformationTypeResponse]);
  const { filtered: filtInventory, paginated: pagInventory } =
    filterAndPaginate(inventoryItems, [
      "description" as keyof InventoryItemResponse,
    ]);
  const { filtered: filtSystemUsers, paginated: pagSystemUsers } =
    filterAndPaginate(systemUsers, ["title" as keyof SspSystemUserResponse]);
  const { filtered: filtInterconnections, paginated: pagInterconnections } =
    filterAndPaginate(interconnections, [
      "title" as keyof SspInterconnectionResponse,
    ]);
  const { filtered: filtLocations, paginated: pagLocations } =
    filterAndPaginate(locations, ["title" as keyof SspLocationResponse]);
  const { filtered: filtLevAuths, paginated: pagLevAuths } = filterAndPaginate(
    leveragedAuths,
    ["title" as keyof LeveragedAuthorizationResponse],
  );

  // SSP Journey metrics — counts and implementation status breakdown derived from loaded state
  const journeyMetrics: JourneyMetrics = (() => {
    const counts = {
      implemented: 0,
      partial: 0,
      planned: 0,
      alternative: 0,
      notImplemented: 0,
      notApplicable: 0,
    };
    // Each unique baseline control with at least one "implemented" requirement counts as implemented.
    const baselineById = new Map<string, string>();
    baselineControls?.forEach((bc: any) => {
      const ctrlId = bc?.controlId || bc?.ciId || bc?.id;
      if (ctrlId) baselineById.set(String(ctrlId), bc?.implementationStatus || "not-implemented");
    });
    // Overlay the live status from controlImpls when present
    controlImpls?.forEach((ci: any) => {
      const cid = ci?.ciId || ci?.controlId;
      if (!cid) return;
      const status = (ci?.implementationStatus || ci?.status || "").toString().toLowerCase();
      if (status) baselineById.set(String(cid), status);
    });
    baselineById.forEach((status) => {
      const s = status.toLowerCase();
      if (s === "implemented") counts.implemented++;
      else if (s === "partial") counts.partial++;
      else if (s === "planned") counts.planned++;
      else if (s === "alternative") counts.alternative++;
      else if (s === "not applicable" || s === "not-applicable" || s === "na") counts.notApplicable++;
      else counts.notImplemented++;
    });

    return {
      implementationCounts: counts,
      totalBaselineControls: baselineById.size,
      controlImplsCount: controlImpls?.length || 0,
      componentsCount: sspComponents?.length || 0,
      infoTypesCount: infoTypes?.length || 0,
      inventoryCount: inventoryItems?.length || 0,
      systemUsersCount: systemUsers?.length || 0,
      interconnectionsCount: interconnections?.length || 0,
      locationsCount: locations?.length || 0,
      partiesCount: parties?.length || 0,
      leveragedAuthsCount: leveragedAuths?.length || 0,
      hasSystemCharacteristics: Boolean(sysChars?.systemName),
    };
  })();

  return (
    <div className="space-y-3">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => {
            setSelectedSSPId(null);
            setSspDetail(null);
            setOrgName(null);
          }}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="size-3.5" />
          System Security Plans
        </button>
        <ChevronRight className="size-3.5 text-muted-foreground" />
        <span className="text-sm font-medium truncate">
          {sspDocTitles[
            sspList.find((s) => s.id === selectedSSPId)?.documentId ?? ""
          ] ||
            sspDetail?.title ||
            `SSP`}
        </span>
        {/* Lifecycle stepper */}
        <div className="ml-3 flex items-center gap-1">
          {LIFECYCLE_STAGES.map((stage, idx) => {
            const isCurrent = stage.key === lifecycle;
            const isPast = currentStageIdx >= 0 && idx < currentStageIdx;
            return (
              <React.Fragment key={stage.key}>
                <div
                  className={`flex items-center gap-1 ${isCurrent ? "text-foreground" : isPast ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground/40"}`}
                >
                  <div
                    className={`size-2 rounded-full ${isCurrent ? "bg-blue-500 ring-2 ring-blue-500/30" : isPast ? "bg-emerald-500" : "bg-muted-foreground/20"}`}
                  />
                  <span
                    className={`text-[10px] ${isCurrent ? "font-bold" : "font-medium"}`}
                  >
                    {stage.label}
                  </span>
                </div>
                {idx < LIFECYCLE_STAGES.length - 1 && (
                  <div
                    className={`w-4 h-px ${isPast ? "bg-emerald-500/50" : "bg-muted-foreground/15"}`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg border border-border bg-card text-sm text-muted-foreground">
          <span>Something went wrong.</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-xs underline hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* SSP Hero Header */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        {/* Top section: title + actions */}
        <div className="px-4 pt-3 pb-2.5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="size-9 rounded-lg bg-indigo-50 dark:bg-indigo-500/15 flex items-center justify-center flex-shrink-0">
                <Shield className="size-4 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                {editingSspTitle ? (
                  <div className="flex items-center gap-1.5">
                    <input
                      autoFocus
                      value={sspTitleDraft}
                      onChange={(e) => setSspTitleDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleRenameSsp();
                        if (e.key === "Escape") setEditingSspTitle(false);
                      }}
                      maxLength={255}
                      disabled={savingSspTitle}
                      className="text-base font-black px-2 py-0.5 rounded border border-indigo-400 bg-card outline-none focus:ring-1 focus:ring-indigo-500 min-w-[220px]"
                    />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={handleRenameSsp}
                          disabled={savingSspTitle || !sspTitleDraft.trim()}
                          className="p-1 rounded hover:bg-green-50 dark:hover:bg-green-500/10 text-green-600 dark:text-green-400 transition-colors disabled:opacity-40"
                        >
                          {savingSspTitle ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <Check className="size-4" />
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Save</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setEditingSspTitle(false)}
                          disabled={savingSspTitle}
                          className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-500/10 text-red-600 dark:text-red-400 transition-colors disabled:opacity-40"
                        >
                          <X className="size-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Cancel</TooltipContent>
                    </Tooltip>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <h2 className="text-base font-black text-foreground tracking-[-0.3px] leading-tight">
                      {currentSspTitle || `SSP`}
                    </h2>
                    {!isPublishedSsp && (
                      <RBACAction module="ssp" action="update">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => {
                                setSspTitleDraft(currentSspTitle);
                                setEditingSspTitle(true);
                              }}
                              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                            >
                              <Pencil className="size-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>Rename SSP</TooltipContent>
                        </Tooltip>
                      </RBACAction>
                    )}
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  System Security Plan
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Lifecycle transition buttons */}
              {currentLifecycleActions.map((action) => {
                const button = (
                  <button
                    key={action.target}
                    onClick={() => openTransitionModal(action.target)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-white text-xs font-semibold transition-colors shadow-md ${action.color}`}
                  >
                    {action.icon}
                    {action.label}
                  </button>
                );

                // Determine required permission based on action label
                if (["Approve", "Reject"].includes(action.label)) {
                  return (
                    <RBACAction key={action.target} module="ssp" action="approve">
                      {button}
                    </RBACAction>
                  );
                }
                if (action.label === "Publish") {
                  return (
                    <RBACAction key={action.target} module="ssp" action="publish">
                      {button}
                    </RBACAction>
                  );
                }
                if (action.label === "Submit for Review") {
                  return (
                    <RBACAction key={action.target} module="ssp" action="update">
                      {button}
                    </RBACAction>
                  );
                }
                if (["Supersede", "Withdraw"].includes(action.label)) {
                  return (
                    <RBACAction key={action.target} module="ssp" action="update">
                      {button}
                    </RBACAction>
                  );
                }
                return button;
              })}
            </div>
          </div>
        </div>

        {/* Info row: profile, status, system ID, lifecycle, updated */}
        <div className="px-4 py-2 bg-muted/30 border-t border-border">
          <div className="flex items-center gap-4 flex-wrap">
            {/* Profile */}
            {(() => {
              const currentSsp = sspList.find((s) => s.id === selectedSSPId);
              const profName = currentSsp?.profileName;
              return profName ? (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold text-foreground tracking-wide">
                    Profile
                  </span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                    {profName}
                  </span>
                </div>
              ) : null;
            })()}

            {/* Source name — shown as "Application Name" or "Service Name" based on sourceType */}
            {(() => {
              const currentSsp = sspList.find((s) => s.id === selectedSSPId);
              const srcName = currentSsp?.sourceName;
              const srcType = (currentSsp?.sourceType || "").toLowerCase();
              if (!srcName) return null;
              const label =
                srcType === "application"
                  ? "Application Name"
                  : srcType === "service"
                    ? "Service Name"
                    : "Source";
              return (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold text-foreground tracking-wide">
                    {label}
                  </span>
                  <span className="text-xs text-foreground">
                    {srcName}
                  </span>
                </div>
              );
            })()}

            {/* Organization name */}
            {(() => {
              // Guard: Ensure orgName is a non-empty string
              if (typeof orgName !== "string" || orgName.trim().length === 0) {
                return null;
              }
              return (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold text-foreground tracking-wide">
                    Organization
                  </span>
                  <span className="text-xs text-foreground">
                    {orgName.trim()}
                  </span>
                </div>
              );
            })()}

            {/* Status from system characteristics */}
            {sysChars?.status && (
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-foreground tracking-wide">
                  Status
                </span>
                <span className="text-xs font-medium px-2.5 py-1 rounded bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">
                  {SYSTEM_STATUSES.find((s) => s.value === sysChars.status)?.label || sysChars.status}
                </span>
              </div>
            )}

            {/* System ID from system characteristics */}
            {sysChars?.systemIds && sysChars.systemIds.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-foreground tracking-wide">
                  System ID
                </span>
                <span className="text-xs font-mono font-medium px-2 py-0.5 rounded bg-muted text-foreground break-all">
                  {String(
                    sysChars.systemIds[0]?.id || sysChars.systemIds[0] || "",
                  )}
                </span>
              </div>
            )}

            {/* Lifecycle status */}
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-foreground tracking-wide">
                Lifecycle
              </span>
              <StatusBadge status={lifecycle as Parameters<typeof StatusBadge>[0]["status"]} />
            </div>

            {/* Updated date */}
            {sspDetail && (
              <span className="text-xs text-muted-foreground ml-auto">
                Updated:{" "}
                {new Date(sspDetail.updatedAt).toLocaleDateString("en-GB")}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* SSP Journey — primary navigation (replaces text tabs) */}
      <SSPDetailJourney
        activeTab={activeTab}
        onTabChange={(tab) => setActiveTab(tab as typeof activeTab)}
        metrics={journeyMetrics}
      />

      {/* Search + Rows per page + Add button – below tabs */}
      {(() => {
        const searchTabs = [
          "Information Types",
          "Components",
          "Inventory",
          "System Users",
          "Interconnections",
          "Locations",
          "Leveraged Authorizations",
        ];
        const paginatedTabs = searchTabs;
        const showSearch = searchTabs.includes(activeTab);
        // "Rows per page" is hidden on the Components tab (commented out per
        // request). It still shows on the other paginated tabs.
        const showRowsPerPage =
          paginatedTabs.includes(activeTab) && activeTab !== "Components";
        const canShowCurrentAddBtn = Boolean(currentAddBtn && !isPublishedSsp);
        if (!showSearch && !showRowsPerPage && !canShowCurrentAddBtn)
          return null;
        return (
          <div className="flex items-center gap-4">
            {showSearch && (
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground/50 pointer-events-none" />
                <input
                  type="text"
                  placeholder={`Search ${formatTabName(activeTab)}`}
                  value={tabSearch}
                  onChange={(e) => {
                    setTabSearch(e.target.value);
                    setTabPage(1);
                  }}
                  className="w-full pl-9 pr-4 py-2 text-sm bg-card border border-border rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:text-muted-foreground/50"
                />
              </div>
            )}
            {showRowsPerPage && (
              <div
                className={`flex items-center gap-2 flex-shrink-0 ${canShowCurrentAddBtn ? "" : "ml-auto"
                  }`}
              >
                <label className="text-sm text-muted-foreground whitespace-nowrap">
                  Rows per page
                </label>
                <Dropdown
                  value={String(tabPageSize)}
                  onChange={(v) => {
                    setCurrentTabPagination({ page: 1, pageSize: Number(v) });
                  }}
                  options={[10, 25, 50, 100].map((n) => ({
                    value: String(n),
                    label: String(n),
                  }))}
                  size="sm"
                  align="end"
                  className="!min-w-[80px]"
                />
              </div>
            )}
            {canShowCurrentAddBtn && currentAddBtn && (
              <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
                <button
                  onClick={currentAddBtn.action}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors"
                >
                  <Plus className="size-3" />
                  {currentAddBtn.label}
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {/* ═══ System Characteristics Tab ═══ */}
      {activeTab === "System Characteristics" && (
        <SystemCharacteristicsTab
          sysCharsLoading={sysCharsLoading}
          sysChars={sysChars}
          setSysChars={setSysChars}
          editingSysChars={editingSysChars}
          setEditingSysChars={setEditingSysChars}
          sysCharsForm={sysCharsForm}
          setSysCharsForm={setSysCharsForm}
          savingSysChars={savingSysChars}
          handleSaveSysChars={handleSaveSysChars}
          isPublishedSsp={isPublishedSsp}
          networkArchDiagram={networkArchDiagram}
          dataFlowDiagram={dataFlowDiagram}
          uploadingDiagram={uploadingDiagram}
          handleDiagramUpload={handleDiagramUpload}
          handleDiagramDelete={handleDiagramDelete}
          setViewingDiagram={setViewingDiagram}
        />
      )}

      {/* ═══ Information Types Tab ═══ */}
      {activeTab === "Information Types" && (
        <InformationTypesTab
          infoTypesLoading={infoTypesLoading}
          infoTypes={infoTypes}
          pagInfoTypes={pagInfoTypes}
          filtInfoTypes={filtInfoTypes}
          editingInfoTypeId={editingInfoTypeId}
          setEditingInfoTypeId={setEditingInfoTypeId}
          editInfoTypeForm={editInfoTypeForm}
          setEditInfoTypeForm={setEditInfoTypeForm}
          savingInfoType={savingInfoType}
          handleUpdateInfoType={handleUpdateInfoType}
          isPublishedSsp={isPublishedSsp}
          requestDelete={requestDelete}
          handleDeleteInfoType={handleDeleteInfoType}
          tabPage={tabPage}
          tabPageSize={tabPageSize}
          setCurrentTabPagination={setCurrentTabPagination}
        />
      )}

      {/* ═══ Inventory Tab ═══ */}
      {activeTab === "Inventory" && (
        <InventoryTab
          inventoryLoading={inventoryLoading}
          inventoryItems={inventoryItems}
          pagInventory={pagInventory}
          filtInventory={filtInventory}
          editingInventoryId={editingInventoryId}
          setEditingInventoryId={setEditingInventoryId}
          editInventoryForm={editInventoryForm}
          setEditInventoryForm={setEditInventoryForm}
          savingInventoryItem={savingInventoryItem}
          handleUpdateInventoryItem={handleUpdateInventoryItem}
          sspComponents={sspComponents}
          isPublishedSsp={isPublishedSsp}
          requestDelete={requestDelete}
          handleDeleteInventoryItem={handleDeleteInventoryItem}
          tabPage={tabPage}
          tabPageSize={tabPageSize}
          setCurrentTabPagination={setCurrentTabPagination}
        />
      )}

      {/* ═══ Components Tab ═══ */}
      {activeTab === "Components" && (
        <ComponentsTab
          selectedComponentUuid={selectedComponentUuid}
          setSelectedComponentUuid={setSelectedComponentUuid}
          sspComponents={sspComponents}
          compInvPage={compInvPage}
          setCompInvPage={setCompInvPage}
          compCtrlPage={compCtrlPage}
          setCompCtrlPage={setCompCtrlPage}
          compPageSize={compPageSize}
          setCompPageSize={setCompPageSize}
          compInventory={compInventory}
          compControls={compControls}
          compDetailLoading={compDetailLoading}
          compDetailTab={compDetailTab}
          setCompDetailTab={setCompDetailTab}
          handleOpenComponentDetail={handleOpenComponentDetail}
          componentsLoading={componentsLoading}
          componentDeleteError={componentDeleteError}
          setComponentDeleteError={setComponentDeleteError}
          componentGroups={componentGroups}
          availableComponentTypes={availableComponentTypes}
          componentTypeFilter={componentTypeFilter}
          setComponentTypeFilter={setComponentTypeFilter}
          availableGroupNames={availableGroupNames}
          componentGroupFilter={componentGroupFilter}
          setComponentGroupFilter={setComponentGroupFilter}
          componentSearchFilter={componentSearchFilter}
          setComponentSearchFilter={setComponentSearchFilter}
          selectedLayerKey={selectedLayerKey}
          setSelectedLayerKey={setSelectedLayerKey}
          isDark={isDark}
          editingComponentId={editingComponentId}
          setEditingComponentId={setEditingComponentId}
          editComponentForm={editComponentForm}
          setEditComponentForm={setEditComponentForm}
          savingComponent={savingComponent}
          handleUpdateComponent={handleUpdateComponent}
          handleDeleteComponent={handleDeleteComponent}
          deletingComponentId={deletingComponentId}
          isPublishedSsp={isPublishedSsp}
          requestDelete={requestDelete}
        />
      )}

      {/* ═══ Control Implementations Tab ═══ */}
      {activeTab === "Control Implementations" && (
        <ControlImplementationsTab
          sspId={selectedSSPId!}
          owningOrgUnitId={
            sspList.find((s) => s.id === selectedSSPId)?.owningOrgUnitId
          }
          sourceType={sspList.find((s) => s.id === selectedSSPId)?.sourceType}
          lifecycleStatus={getSelectedSspLifecycle()}
        />
      )}


      {/* ═══ Leveraged Authorizations Tab ═══ */}
      {activeTab === "Leveraged Authorizations" && (
        <LeveragedAuthorizationsTab
          leveragedAuthsLoading={leveragedAuthsLoading}
          leveragedAuths={leveragedAuths}
          pagLevAuths={pagLevAuths}
          filtLevAuths={filtLevAuths}
          editingLevAuthId={editingLevAuthId}
          setEditingLevAuthId={setEditingLevAuthId}
          editLevAuthForm={editLevAuthForm}
          setEditLevAuthForm={setEditLevAuthForm}
          savingLevAuth={savingLevAuth}
          handleUpdateLevAuth={handleUpdateLevAuth}
          parties={parties}
          openLevAuthMenuId={openLevAuthMenuId}
          setOpenLevAuthMenuId={setOpenLevAuthMenuId}
          updatingLevAuthStatusId={updatingLevAuthStatusId}
          handleUpdateLevAuthStatus={handleUpdateLevAuthStatus}
          tabPage={tabPage}
          tabPageSize={tabPageSize}
          setCurrentTabPagination={setCurrentTabPagination}
        />
      )}

      {/* ═══ System Users Tab ═══ */}
      {activeTab === "System Users" && (
        <SystemUsersTab
          systemUsersLoading={systemUsersLoading}
          systemUsers={systemUsers}
          pagSystemUsers={pagSystemUsers}
          filtSystemUsers={filtSystemUsers}
          editingSystemUserId={editingSystemUserId}
          setEditingSystemUserId={setEditingSystemUserId}
          editSystemUserForm={editSystemUserForm}
          setEditSystemUserForm={setEditSystemUserForm}
          savingSystemUser={savingSystemUser}
          handleUpdateSystemUser={handleUpdateSystemUser}
          isPublishedSsp={isPublishedSsp}
          handleDeleteSystemUser={handleDeleteSystemUser}
          deletingSystemUserId={deletingSystemUserId}
          tabPage={tabPage}
          tabPageSize={tabPageSize}
          setCurrentTabPagination={setCurrentTabPagination}
        />
      )}

      {/* ═══ Interconnections Tab ═══ */}
      {activeTab === "Interconnections" && (
        <InterconnectionsTab
          interconnectionsLoading={interconnectionsLoading}
          interconnections={interconnections}
          pagInterconnections={pagInterconnections}
          filtInterconnections={filtInterconnections}
          editingInterconnectionId={editingInterconnectionId}
          setEditingInterconnectionId={setEditingInterconnectionId}
          editInterconnectionForm={editInterconnectionForm}
          setEditInterconnectionForm={setEditInterconnectionForm}
          savingInterconnection={savingInterconnection}
          handleUpdateInterconnection={handleUpdateInterconnection}
          isPublishedSsp={isPublishedSsp}
          handleDeleteInterconnection={handleDeleteInterconnection}
          deletingInterconnectionId={deletingInterconnectionId}
          tabPage={tabPage}
          tabPageSize={tabPageSize}
          setCurrentTabPagination={setCurrentTabPagination}
        />
      )}

      {/* ═══ Roles & Parties Tab ═══ */}
      {activeTab === "Roles & Parties" && (
        <RolesPartiesTab
          rolesPartiesLoading={rolesPartiesLoading}
          isPublishedSsp={isPublishedSsp}
          requestDelete={requestDelete}
          roles={roles}
          rolesPagination={rolesPagination}
          setRolesPagination={setRolesPagination}
          editingRoleId={editingRoleId}
          setEditingRoleId={setEditingRoleId}
          editRoleForm={editRoleForm}
          setEditRoleForm={setEditRoleForm}
          savingRole={savingRole}
          handleUpdateRole={handleUpdateRole}
          deletingRoleId={deletingRoleId}
          handleDeleteRole={handleDeleteRole}
          setShowAddRole={setShowAddRole}
          parties={parties}
          partiesPagination={partiesPagination}
          setPartiesPagination={setPartiesPagination}
          editingPartyId={editingPartyId}
          setEditingPartyId={setEditingPartyId}
          editPartyForm={editPartyForm}
          setEditPartyForm={setEditPartyForm}
          savingParty={savingParty}
          handleUpdateParty={handleUpdateParty}
          deletingPartyId={deletingPartyId}
          handleDeleteParty={handleDeleteParty}
          setShowAddParty={setShowAddParty}
          responsibleParties={responsibleParties}
          deletingRPId={deletingRPId}
          handleDeleteRP={handleDeleteRP}
          setShowAssignRP={setShowAssignRP}
        />
      )}

      {/* ═══ Locations Tab ═══ */}
      {activeTab === "Locations" && (
        <LocationsTab
          locationsLoading={locationsLoading}
          locations={locations}
          pagLocations={pagLocations}
          filtLocations={filtLocations}
          editingLocationId={editingLocationId}
          setEditingLocationId={setEditingLocationId}
          editLocationForm={editLocationForm}
          setEditLocationForm={setEditLocationForm}
          savingLocation={savingLocation}
          handleUpdateLocation={handleUpdateLocation}
          isPublishedSsp={isPublishedSsp}
          requestDelete={requestDelete}
          deletingLocationId={deletingLocationId}
          handleDeleteLocation={handleDeleteLocation}
          tabPage={tabPage}
          tabPageSize={tabPageSize}
          setCurrentTabPagination={setCurrentTabPagination}
        />
      )}



      {/* ═══ MODALS ═══ */}

      {/* OSCAL Export Modal */}
      <ExportDialog
        open={showExport}
        onClose={() => {
          setShowExport(false);
          setExportTargetDocId(null);
        }}
        exportingDoc={exportingDoc}
        handleExport={handleExport}
      />

      {/* Export Pulse Overlay */}
      {exportingDoc && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4 bg-card rounded-2xl px-10 py-8 shadow-2xl">
            <div className="relative flex items-center justify-center">
              <div className="absolute size-16 rounded-full bg-indigo-500/20 dark:bg-indigo-500/20 animate-ping" />
              <div className="absolute size-12 rounded-full bg-indigo-500/30 dark:bg-indigo-500/30 animate-pulse" />
              <Upload className="relative size-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <p className="text-sm font-semibold text-foreground mt-2">
              Exporting as {exportFormat.toUpperCase()}
            </p>
            <p className="text-xs text-muted-foreground">
              Please wait while we prepare your file
            </p>
          </div>
        </div>
      )}

      {/* ── Import SSP Modal (editor view) — same component as list-view ── */}
      <ImportSspDialog
        open={showSspImport}
        onClose={() => {
          setShowSspImport(false);
          resetSspImport();
        }}
        resetSspImport={resetSspImport}
        sspImportStep={sspImportStep}
        setSspImportStep={setSspImportStep}
        sspImportFile={sspImportFile}
        setSspImportFile={setSspImportFile}
        sspImportUrl={sspImportUrl}
        setSspImportUrl={setSspImportUrl}
        sspImportMode={sspImportMode}
        setSspImportMode={setSspImportMode}
        sspValidateResult={sspValidateResult}
        setSspValidateResult={setSspValidateResult}
        sspValidating={sspValidating}
        setSspValidating={setSspValidating}
        sspImportError={sspImportError}
        setSspImportError={setSspImportError}
        sspImportErrorTitle={sspImportErrorTitle}
        sspRunImport={sspRunImport}
        sspStartValidation={sspStartValidation}
        sspFetchUrl={sspFetchUrl}
      />

      {/* ── Import Session Modal (editor view) — same component as list-view ── */}
      <ImportSessionDialog
        open={showSspSession}
        onClose={() => setShowSspSession(false)}
        sspSessionData={sspSessionData}
        setSspSessionData={setSspSessionData}
        sspSessionLoading={sspSessionLoading}
        sspSessionError={sspSessionError}
        sspCommitting={sspCommitting}
        sspAborting={sspAborting}
        handleSspCommitSession={handleSspCommitSession}
        handleSspAbortSession={handleSspAbortSession}
        resetSspImport={resetSspImport}
        onCommittedDone={() => {
          setShowSspSession(false);
        }}
      />

      {/* Add Information Type Modal */}
      <AddInformationTypeDialog
        open={showAddInfoType}
        onClose={() => setShowAddInfoType(false)}
        handleAddInfoType={handleAddInfoType}
        addingInfoType={addingInfoType}
        infoTypeForm={infoTypeForm}
        setInfoTypeForm={setInfoTypeForm}
      />

      {/* Add Component Modal */}
      <AddComponentDialog
        open={showAddComponent}
        onClose={() => setShowAddComponent(false)}
        handleAddComponent={handleAddComponent}
        addingComponent={addingComponent}
        componentForm={componentForm}
        setComponentForm={setComponentForm}
      />

      {/* Add Inventory Item Modal */}
      <AddInventoryItemDialog
        open={showAddAsset}
        onClose={() => setShowAddAsset(false)}
        handleAddAsset={handleAddAsset}
        addingAsset={addingAsset}
        assetForm={assetForm}
        setAssetForm={setAssetForm}
        sspComponents={sspComponents}
      />

      {/* Add Leveraged Authorization Modal */}
      <AddLeveragedAuthorizationDialog
        open={showAddLevAuth}
        onClose={() => setShowAddLevAuth(false)}
        handleAddLevAuth={handleAddLevAuth}
        addingLevAuth={addingLevAuth}
        levAuthForm={levAuthForm}
        setLevAuthForm={setLevAuthForm}
        parties={parties}
      />

      {/* Add Control Implementation Modal */}
      <AddControlImplementationDialog
        open={showAddControlImpl}
        onClose={() => setShowAddControlImpl(false)}
        handleAddControlImpl={handleAddControlImpl}
        addingControlImpl={addingControlImpl}
        controlImplForm={controlImplForm}
        setControlImplForm={setControlImplForm}
        sspComponents={sspComponents}
        ciControls={ciControls}
        ciLoadingControls={ciLoadingControls}
        ciCatalogId={ciCatalogId}
        ciSelectedControlDetail={ciSelectedControlDetail}
        setCiSelectedControlDetail={setCiSelectedControlDetail}
        ciLoadingControlDetail={ciLoadingControlDetail}
        setCiLoadingControlDetail={setCiLoadingControlDetail}
      />

      {/* Edit Control Implementation Modal */}
      <EditControlImplementationDialog
        open={showEditControlImpl}
        onClose={() => {
          setShowEditControlImpl(false);
          setEditingControlImplId(null);
        }}
        handleUpdateControlImpl={handleUpdateControlImpl}
        savingControlImpl={savingControlImpl}
        editControlImplForm={editControlImplForm}
        setEditControlImplForm={setEditControlImplForm}
        sspComponents={sspComponents}
        baselineControls={baselineControls}
      />

      {/* Add Role Modal */}
      <AddRoleDialog
        open={showAddRole}
        onClose={() => setShowAddRole(false)}
        handleAddRole={handleAddRole}
        addingRole={addingRole}
        roleForm={roleForm}
        setRoleForm={setRoleForm}
      />

      {/* Add Party Modal */}
      <AddPartyDialog
        open={showAddParty}
        onClose={() => setShowAddParty(false)}
        handleAddParty={handleAddParty}
        addingParty={addingParty}
        partyForm={partyForm}
        setPartyForm={setPartyForm}
      />

      {/* Assign Responsible Party Modal */}
      <AssignResponsiblePartyDialog
        open={showAssignRP}
        onClose={() => setShowAssignRP(false)}
        handleAssignRP={handleAssignRP}
        assigningRP={assigningRP}
        rpForm={rpForm}
        setRpForm={setRpForm}
        roles={roles}
        parties={parties}
      />

      {/* Add Location Modal */}
      <AddLocationDialog
        open={showAddLocation}
        onClose={() => setShowAddLocation(false)}
        handleAddLocation={handleAddLocation}
        addingLocation={addingLocation}
        locationForm={locationForm}
        setLocationForm={setLocationForm}
      />

      {/* Add System User Modal */}
      <AddSystemUserDialog
        open={showAddSystemUser}
        onClose={() => setShowAddSystemUser(false)}
        handleAddSystemUser={handleAddSystemUser}
        addingSystemUser={addingSystemUser}
        systemUserForm={systemUserForm}
        setSystemUserForm={setSystemUserForm}
      />

      {/* Add Interconnection Modal */}
      <AddInterconnectionDialog
        open={showAddInterconnection}
        onClose={() => setShowAddInterconnection(false)}
        handleAddInterconnection={handleAddInterconnection}
        addingInterconnection={addingInterconnection}
        interconnectionForm={interconnectionForm}
        setInterconnectionForm={setInterconnectionForm}
      />

      {/* Lifecycle Transition Modal */}
      <LifecycleTransitionDialog
        open={showTransitionModal}
        onClose={() => setShowTransitionModal(false)}
        handleLifecycleTransition={handleLifecycleTransition}
        transitioningLifecycle={transitioningLifecycle}
        transitionTarget={transitionTarget}
        transitionComment={transitionComment}
        setTransitionComment={setTransitionComment}
        currentLifecycleActions={currentLifecycleActions}
        sspDetail={sspDetail}
      />

      {/* Import Component from Library Modal */}
      <ImportComponentFromLibraryDialog
        open={showImportComponent}
        onClose={() => {
          setShowImportComponent(false);
          setSelectedImportComponentIds(new Set());
        }}
        handleImportComponents={handleImportComponents}
        importingComponents={importingComponents}
        selectedImportComponentIds={selectedImportComponentIds}
        setSelectedImportComponentIds={setSelectedImportComponentIds}
        sspComponents={sspComponents}
        cdefComponents={cdefComponents}
        cdefLoading={cdefLoading}
        importedComponentIds={importedComponentIds}
      />

      {/* Post-Import Control Status Popup - Split Panel */}
      <PostImportControlStatusDialog
        open={showPostImportPopup}
        onClose={() => setShowPostImportPopup(false)}
        closePostImportPopups={closePostImportPopups}
        postImportComponentTitle={postImportComponentTitle}
        postImportIRs={postImportIRs}
        postImportLoading={postImportLoading}
        postImportSearch={postImportSearch}
        setPostImportSearch={setPostImportSearch}
        postImportSelectedIdx={postImportSelectedIdx}
        setPostImportSelectedIdx={setPostImportSelectedIdx}
        handleSelectPostImportControl={handleSelectPostImportControl}
        postImportDescriptions={postImportDescriptions}
        setPostImportDescriptions={setPostImportDescriptions}
        postImportStatuses={postImportStatuses}
        setPostImportStatuses={setPostImportStatuses}
        postImportStmtEdits={postImportStmtEdits}
        postImportControlDetails={postImportControlDetails}
        postImportLoadingControlId={postImportLoadingControlId}
        postImportParamEdits={postImportParamEdits}
        setPostImportParamEdits={setPostImportParamEdits}
        excludedIrIds={excludedIrIds}
        setExcludedIrIds={setExcludedIrIds}
        savedIrIds={savedIrIds}
        savingIrId={savingIrId}
        bulkSaving={bulkSaving}
        handleBulkSaveAll={handleBulkSaveAll}
        handleSaveSingleIR={handleSaveSingleIR}
      />

      {/* Diagram Viewer Modal */}
      {viewingDiagram && (
        <DiagramPreviewModal
          url={previewSupportingEvidenceFile(viewingDiagram.contentEvidenceId)}
          fileName={viewingDiagram.fileName ?? null}
          downloadUrl={downloadSupportingEvidenceFile(
            viewingDiagram.contentEvidenceId,
          )}
          title={viewingDiagram.title}
          onClose={() => setViewingDiagram(null)}
        />
      )}

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={!!deleteTarget}
        type="SSP"
        name={
          deleteTarget
            ? sspDocTitles[deleteTarget.documentId] || deleteTarget.documentId
            : ""
        }
        deleting={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => handleDeleteSSP()}
      />

      {/* Generic Delete Confirmation */}
      <DeleteConfirmDialog
        open={!!pendingDelete}
        type={pendingDelete?.type || "Item"}
        name={pendingDelete?.title || ""}
        deleting={pendingDeleting}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => confirmPendingDelete()}
      />
    </div>
  );
}
